import { useState, useEffect, useMemo, useRef } from 'react';
import { beatDetails } from './data/beatDetails';
import { arcDetails } from './data/arcDetails';
import { sequenceDetails } from './data/sequenceDetails';
import { knowledgeContent } from './data/knowledgeContent';
import { STORY_TYPES, getStoryType } from './data/storyTypes';

// Audio content configuration
const audioContent = {
  storyOverview: {
    title: "Story Overview",
    url: "/audio/knowledge/story-overview.m4a"
  },
  guideOverview: {
    title: "Welcome Guide",
    url: "/audio/knowledge/welcome-guide.m4a"
  }
};


// Data arrays for character roles
const tangibleRoles = [
  'Mentor', 'Partner', 'Love Interest', 'Sidekick', 'Rival', 'Competitor', 
  'Backstabber', 'Authority/Ruler', 'Confidant', 'Protector/Guardian', 'Innocent/Dependent', 
  'Handler', 'Gatekeeper'
];

const thematicRoles = [
  'Believer', 'Bender', 'Doubter', 'Hater', 'Wildcard'
];

// Utility function for sorting characters
const sortCharacters = (characters) => {
  return [...characters].sort((a, b) => {
    // Neutral first, then major, then minor
    if (a.major === null && b.major !== null) return -1;
    if (a.major !== null && b.major === null) return 1;
    if (a.major === true && b.major === false) return -1;
    if (a.major === false && b.major === true) return 1;
    return a.id.localeCompare(b.id);
  });
};

// Improved Three-State Toggle Component with better accessibility
const ThreeStateToggle = ({ value, onChange, characterId }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  
  const handleToggleClick = (newValue) => {
    if (value !== newValue && (value === null || newValue !== null)) {
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 600);
    }
    onChange(newValue);
  };

  const getTogglePosition = () => {
    if (value === true) return '3px'; // Major (left)
    if (value === false) return '53px'; // Minor (right)
    return '28px'; // Neutral (middle)
  };

  const getBackgroundColor = () => {
    if (value === true) return '#FF8C42'; // Orange for major
    if (value === false) return '#58d68d'; // Green for minor
    return '#95a5a6'; // Gray for neutral
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{ 
        fontSize: '12px', 
        fontWeight: '500',
        color: value === true ? '#FF8C42' : '#999',
        transition: 'color 0.3s ease'
      }}>
        Major
      </span>
      
      <label style={{
        position: 'relative',
        display: 'inline-block',
        width: '76px',
        height: '24px',
        cursor: 'pointer'
      }}>
        <input
          type="checkbox"
          style={{ opacity: 0, width: 0, height: 0 }}
          checked={value !== null}
          onChange={() => {}} // Controlled by click handlers
          aria-label={`Character importance: ${value === true ? 'Major' : value === false ? 'Minor' : 'Neutral'}`}
        />
        
        {/* Background track */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: getBackgroundColor(),
            borderRadius: '24px',
            transition: 'background-color 0.3s ease',
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          {/* Clickable zones */}
          <div
            onClick={() => handleToggleClick(true)}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: '33%',
              height: '100%',
              cursor: 'pointer',
              zIndex: 2
            }}
            aria-label="Set as major character"
          />
          <div
            onClick={() => handleToggleClick(null)}
            style={{
              position: 'absolute',
              left: '33%',
              top: 0,
              width: '34%',
              height: '100%',
              cursor: 'pointer',
              zIndex: 2
            }}
            aria-label="Set as draft character"
          />
          <div
            onClick={() => handleToggleClick(false)}
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              width: '33%',
              height: '100%',
              cursor: 'pointer',
              zIndex: 2
            }}
            aria-label="Set as minor character"
          />
          
          {/* Moving toggle ball */}
          <span style={{
            position: 'absolute',
            height: '18px',
            width: '18px',
            left: getTogglePosition(),
            bottom: '3px',
            backgroundColor: 'white',
            borderRadius: '50%',
            transition: 'left 0.3s ease',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            zIndex: 1,
            pointerEvents: 'none'
          }} />
        </div>
      </label>
      
      <span style={{ 
        fontSize: '12px', 
        fontWeight: '500',
        color: value === false ? '#58d68d' : '#999',
        transition: 'color 0.3s ease'
      }}>
        Minor
      </span>
    </div>
  );
};

// Character Card with Highlight Animation
const CharacterCard = ({ character, updateSecondaryCharacter, removeSecondaryCharacter }) => {
  const [shouldHighlight, setShouldHighlight] = useState(false);
  const [prevMajorState, setPrevMajorState] = useState(character.major);

  // Trigger highlight when major state changes from null to non-null
  useEffect(() => {
    if (prevMajorState === null && character.major !== null) {
      setShouldHighlight(true);
      setTimeout(() => setShouldHighlight(false), 2000);
    }
    setPrevMajorState(character.major);
  }, [character.major, prevMajorState]);

  const getBorderColor = () => {
    if (character.major === true) return '#FF8C42'; // Orange for major
    if (character.major === false) return '#58d68d'; // Green for minor
    return '#95a5a6'; // Gray for neutral
  };

  const getBackgroundColor = () => {
    if (shouldHighlight) return '#fff3cd'; // Highlight color
    if (character.major === true) return '#fff4f0'; // Light orange for major
    if (character.major === false) return '#f0fff4'; // Light green for minor
    return '#f8f9fa'; // Light gray for neutral
  };

  const getBadgeText = () => {
    if (character.major === true) return 'MAJOR CHARACTER';
    if (character.major === false) return 'MINOR CHARACTER';
    return 'DRAFT CHARACTER';
  };

  const getBadgeColor = () => {
    if (character.major === true) return '#FF8C42';
    if (character.major === false) return '#58d68d';
    return '#95a5a6';
  };

  return (
    <div 
      style={{
        padding: '24px',
        border: `3px solid ${getBorderColor()}`,
        borderRadius: '12px',
        backgroundColor: getBackgroundColor(),
        position: 'relative',
        transition: 'all 0.6s ease',
        transform: shouldHighlight ? 'scale(1.02)' : 'scale(1)',
        boxShadow: shouldHighlight ? '0 8px 25px rgba(52, 152, 219, 0.3)' : '0 4px 15px rgba(0,0,0,0.1)'
      }}
    >
      <div style={{
        position: 'absolute',
        top: '-12px',
        left: '20px',
        backgroundColor: getBadgeColor(),
        color: 'white',
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
        transition: 'all 0.3s ease'
      }}>
        {getBadgeText()}
      </div>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <input
            type="text"
            style={{
              padding: '8px 12px',
              border: '2px solid #dee2e6',
              borderRadius: '6px',
              fontSize: '18px',
              fontWeight: '600',
              outline: 'none',
              minWidth: '200px'
            }}
            value={character.name}
            onChange={(e) => updateSecondaryCharacter(character.id, 'name', e.target.value)}
            placeholder="Character name"
          />
          
          {/* Role Dropdowns */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <select
              value={character.tangibleRole || ''}
              onChange={(e) => updateSecondaryCharacter(character.id, 'tangibleRole', e.target.value)}
              style={{
                padding: '6px 10px',
                border: '2px solid #dee2e6',
                borderRadius: '6px',
                fontSize: '12px',
                outline: 'none',
                cursor: 'pointer',
                minWidth: '120px',
                backgroundColor: character.tangibleRole ? getBorderColor() : 'white',
                color: character.tangibleRole ? 'white' : 'black',
                fontWeight: character.tangibleRole ? '600' : 'normal'
              }}
            >
              <option value="">Tangible Role...</option>
              {tangibleRoles.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
            
            <select
              value={character.thematicRole || ''}
              onChange={(e) => updateSecondaryCharacter(character.id, 'thematicRole', e.target.value)}
              style={{
                padding: '6px 10px',
                border: '2px solid #dee2e6',
                borderRadius: '6px',
                fontSize: '12px',
                outline: 'none',
                cursor: 'pointer',
                minWidth: '110px',
                backgroundColor: character.thematicRole ? getBorderColor() : 'white',
                color: character.thematicRole ? 'white' : 'black',
                fontWeight: character.thematicRole ? '600' : 'normal'
              }}
            >
              <option value="">Thematic Role...</option>
              {thematicRoles.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <ThreeStateToggle
            value={character.major}
            onChange={(value) => updateSecondaryCharacter(character.id, 'major', value)}
            characterId={character.id}
          />
          
          <button
            onClick={() => removeSecondaryCharacter(character.id)}
            style={{
              background: '#e74c3c',
              border: 'none',
              borderRadius: '6px',
              color: 'white',
              padding: '8px 12px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#c0392b'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#e74c3c'}
          >
            Remove
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#495057' }}>
            Relationship to Protagonist
          </label>
          <textarea
            style={{
              width: '100%',
              padding: '10px',
              border: '2px solid #dee2e6',
              borderRadius: '6px',
              height: '80px',
              fontSize: '14px',
              resize: 'vertical',
              outline: 'none',
              boxSizing: 'border-box'
            }}
            value={character.relationshipToProtagonist}
            onChange={(e) => updateSecondaryCharacter(character.id, 'relationshipToProtagonist', e.target.value)}
            placeholder="How do they know the protagonist?"
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#495057' }}>
            Story Function
          </label>
          <textarea
            style={{
              width: '100%',
              padding: '10px',
              border: '2px solid #dee2e6',
              borderRadius: '6px',
              height: '80px',
              fontSize: '14px',
              resize: 'vertical',
              outline: 'none',
              boxSizing: 'border-box'
            }}
            value={character.storyFunction}
            onChange={(e) => updateSecondaryCharacter(character.id, 'storyFunction', e.target.value)}
            placeholder="What role do they play in the story?"
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#495057' }}>
            Thematic Function
          </label>
          <textarea
            style={{
              width: '100%',
              padding: '10px',
              border: '2px solid #dee2e6',
              borderRadius: '6px',
              height: '80px',
              fontSize: '14px',
              resize: 'vertical',
              outline: 'none',
              boxSizing: 'border-box'
            }}
            value={character.thematicFunction}
            onChange={(e) => updateSecondaryCharacter(character.id, 'thematicFunction', e.target.value)}
            placeholder="How do they represent or challenge the theme?"
          />
        </div>
      </div>

      <div>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#495057' }}>
          Description & Notes
        </label>
        <textarea
          style={{
            width: '100%',
            padding: '10px',
            border: '2px solid #dee2e6',
            borderRadius: '6px',
            height: '100px',
            fontSize: '14px',
            resize: 'vertical',
            outline: 'none',
            boxSizing: 'border-box'
          }}
          value={character.description}
          onChange={(e) => updateSecondaryCharacter(character.id, 'description', e.target.value)}
          placeholder="Describe this character's personality, appearance, background, or any other details..."
        />
      </div>
    </div>
  );
};

// Updated Characters Component with performance optimizations
const Characters = ({ 
  story, 
  updateStory, 
  addSecondaryCharacter, 
  updateSecondaryCharacter, 
  removeSecondaryCharacter,
  setSelectedKnowledge 
}) => {
  const [sortFilter, setSortFilter] = useState('creation');

  /// Optimized sorting functions with better performance
  const sortCharactersByFilter = useMemo(() => {
  return (characters, filter) => {
    const chars = [...characters];
    
    switch (filter) {
      case 'creation':
  return chars.sort((a, b) => {
    // New characters always first
    if (a.isNew && !b.isNew) return -1;
    if (!a.isNew && b.isNew) return 1;
    
    // For non-new characters, sort purely by creation order (ID)
    // Newer characters (higher timestamp IDs) should come first
    return b.id.localeCompare(a.id);
  });
        
      case 'alphabetical':
  return chars.sort((a, b) => {
    if (a.isNew && !b.isNew) return -1;
    if (!a.isNew && b.isNew) return 1;
    if (!a.name && !b.name) return 0;
    if (!a.name) return 1;
    if (!b.name) return -1;
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
  });
        
      case 'majorFirst':
  return chars.sort((a, b) => {
    // New characters always first
    if (a.isNew && !b.isNew) return -1;
    if (!a.isNew && b.isNew) return 1;
    
    // Then sort by major status: Major -> Minor -> Draft
    if (a.major === true && b.major !== true) return -1;
    if (a.major !== true && b.major === true) return 1;
    if (a.major === false && b.major === null) return -1;
    if (a.major === null && b.major === false) return 1;
    
    // If same major status, sort by creation order
    return a.id.localeCompare(b.id);
  });
        
      case 'minorFirst':
  return chars.sort((a, b) => {
    // New characters always first
    if (a.isNew && !b.isNew) return -1;
    if (!a.isNew && b.isNew) return 1;
    
    // Then sort by major status: Minor (false) -> Major (true) -> Draft (null)
    if (a.major === false && b.major !== false) return -1;
    if (a.major !== false && b.major === false) return 1;
    if (a.major === true && b.major === null) return -1;
    if (a.major === null && b.major === true) return 1;
    
    // If same major status, sort by creation order
    return a.id.localeCompare(b.id);
  });
        
      default:
  return chars.sort((a, b) => {
    // New characters always first
    if (a.isNew && !b.isNew) return -1;
    if (!a.isNew && b.isNew) return 1;
    
    // For non-new characters, sort purely by creation order (ID)
    return a.id.localeCompare(b.id);
  });
    }
  };
}, []);
  

  // Memoize sorted characters to prevent unnecessary re-renders
  const sortedCharacters = useMemo(() => {
    return sortCharactersByFilter(story.secondaryCharacters, sortFilter);
  }, [story.secondaryCharacters, sortFilter, sortCharactersByFilter]);

  // Handle creating blank character
  const handleAddCharacter = () => {
    addSecondaryCharacter('', ''); // Create blank character (neutral by default)
  };

  return (
    <div style={{ padding: '24px', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
        <h2 style={{ fontSize: '36px', fontWeight: 'bold', margin: 0, color: '#2c3e50' }}>Character Development</h2>
        <span 
          onClick={() => setSelectedKnowledge('characterDevelopment')}
          style={{ 
            fontSize: '20px', 
            cursor: 'pointer', 
            padding: '4px',
            borderRadius: '4px',
            transition: 'all 0.2s ease',
            transform: 'scale(1)'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f8f9fa';
            e.target.style.transform = 'scale(1.15)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.transform = 'scale(1)';
          }}
        >
          📖
        </span>
      </div>
      <p className="guide-description" style={{ 
        fontSize: '18px', 
        color: '#666', 
        margin: '0 0 32px 0', 
        lineHeight: '1.5' 
      }}>
        Characters are the heart of your story. Develop compelling protagonists, memorable antagonists, and supporting characters that bring your narrative to life.
      </p>
      
      {/* Protagonist Section */}
      <div style={{
        marginBottom: '32px', backgroundColor: 'white', padding: '32px',
        borderRadius: '16px', boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
        border: '3px solid #3498db',
        position: 'relative'
      }}>
        <div style={{
          position: 'absolute',
          top: '-12px',
          left: '20px',
          backgroundColor: '#3498db',
          color: 'white',
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600'
        }}>
          PRIMARY CHARACTER
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '28px', fontWeight: '600', margin: 0, color: '#495057' }}>Protagonist</h3>
          <span 
            onClick={() => setSelectedKnowledge('protagonist')}
            style={{ 
              fontSize: '20px', 
              cursor: 'pointer', 
              padding: '4px',
              borderRadius: '4px',
              transition: 'all 0.2s ease',
              transform: 'scale(1)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f8f9fa';
              e.target.style.transform = 'scale(1.15)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.transform = 'scale(1)';
            }}
          >
            📖
          </span>
        </div>
        
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontSize: '16px', fontWeight: '500', marginBottom: '8px', color: '#495057' }}>
            Character Name
          </label>
          <input
            type="text"
            style={{ 
              width: '100%', 
              padding: '12px 16px', 
              border: '2px solid #dee2e6', 
              borderRadius: '8px',
              fontSize: '16px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
            value={story.protagonist.name}
            onChange={(e) => updateStory('protagonist', 'name', e.target.value)}
            placeholder="Enter character name"
          />
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <label style={{ display: 'block', fontSize: '16px', fontWeight: '500', color: '#495057' }}>
                Want (Tangible Story)
              </label>
              <span 
                onClick={() => setSelectedKnowledge('protagonistWant')}
                style={{ 
                  fontSize: '16px', 
                  cursor: 'pointer', 
                  padding: '2px',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease',
                  transform: 'scale(1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f8f9fa';
                  e.target.style.transform = 'scale(1.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                📖
              </span>
            </div>
            <textarea
              style={{ 
                width: '100%', 
                padding: '12px 16px', 
                border: '2px solid #dee2e6', 
                borderRadius: '8px', 
                height: '120px',
                fontSize: '16px',
                resize: 'vertical',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              value={story.protagonist.want}
              onChange={(e) => updateStory('protagonist', 'want', e.target.value)}
              placeholder="What concrete goal does your protagonist pursue?"
            />
          </div>
          
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <label style={{ display: 'block', fontSize: '16px', fontWeight: '500', color: '#495057' }}>
                Need (Thematic Story)
              </label>
              <span 
                onClick={() => setSelectedKnowledge('protagonistNeed')}
                style={{ 
                  fontSize: '16px', 
                  cursor: 'pointer', 
                  padding: '2px',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease',
                  transform: 'scale(1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f8f9fa';
                  e.target.style.transform = 'scale(1.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                📖
              </span>
            </div>
            <textarea
              style={{ 
                width: '100%', 
                padding: '12px 16px', 
                border: '2px solid #dee2e6', 
                borderRadius: '8px', 
                height: '120px',
                fontSize: '16px',
                resize: 'vertical',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              value={story.protagonist.need}
              onChange={(e) => updateStory('protagonist', 'need', e.target.value)}
              placeholder="What lesson must they learn to grow or transform?"
            />
          </div>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginTop: '24px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <label style={{ display: 'block', fontSize: '16px', fontWeight: '500', color: '#495057' }}>
                Motivation (Reason for actions and/or beliefs)
              </label>
              <span 
                onClick={() => setSelectedKnowledge('protagonistMotivation')}
                style={{ 
                  fontSize: '16px', 
                  cursor: 'pointer', 
                  padding: '2px',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease',
                  transform: 'scale(1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f8f9fa';
                  e.target.style.transform = 'scale(1.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                📖
              </span>
            </div>
            <textarea
              style={{ 
                width: '100%', 
                padding: '12px 16px', 
                border: '2px solid #dee2e6', 
                borderRadius: '8px', 
                height: '120px',
                fontSize: '16px',
                resize: 'vertical',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              value={story.protagonist.motivation}
              onChange={(e) => updateStory('protagonist', 'motivation', e.target.value)}
              placeholder="What past experience defines how they act and react now?"
            />
          </div>
          
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <label style={{ display: 'block', fontSize: '16px', fontWeight: '500', color: '#495057' }}>
                Desire (Innermost hope or wish)
              </label>
              <span 
                onClick={() => setSelectedKnowledge('protagonistDesire')}
                style={{ 
                  fontSize: '16px', 
                  cursor: 'pointer', 
                  padding: '2px',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease',
                  transform: 'scale(1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f8f9fa';
                  e.target.style.transform = 'scale(1.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                📖
              </span>
            </div>
            <textarea
              style={{ 
                width: '100%', 
                padding: '12px 16px', 
                border: '2px solid #dee2e6', 
                borderRadius: '8px', 
                height: '120px',
                fontSize: '16px',
                resize: 'vertical',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              value={story.protagonist.desire}
              onChange={(e) => updateStory('protagonist', 'desire', e.target.value)}
              placeholder="What personal dream or longing do they yearn for, even if they don't fully admit it?"
            />
          </div>
        </div>
      </div>
      
      {/* Antagonist Section */}
      <div style={{
        marginBottom: '32px', backgroundColor: 'white', padding: '32px',
        borderRadius: '16px', boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
        border: '3px solid #e74c3c',
        position: 'relative'
      }}>
        <div style={{
          position: 'absolute',
          top: '-12px',
          left: '20px',
          backgroundColor: '#e74c3c',
          color: 'white',
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600'
        }}>
          PRIMARY CHARACTER
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '28px', fontWeight: '600', margin: 0, color: '#495057' }}>Antagonist</h3>
          <span 
            onClick={() => setSelectedKnowledge('antagonist')}
            style={{ 
              fontSize: '20px', 
              cursor: 'pointer', 
              padding: '4px',
              borderRadius: '4px',
              transition: 'all 0.2s ease',
              transform: 'scale(1)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f8f9fa';
              e.target.style.transform = 'scale(1.15)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.transform = 'scale(1)';
            }}
          >
            📖
          </span>
        </div>
        
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontSize: '16px', fontWeight: '500', marginBottom: '8px', color: '#495057' }}>
            Character Name
          </label>
          <input
            type="text"
            style={{ 
              width: '100%', 
              padding: '12px 16px', 
              border: '2px solid #dee2e6', 
              borderRadius: '8px',
              fontSize: '16px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
            value={story.antagonist.name}
            onChange={(e) => updateStory('antagonist', 'name', e.target.value)}
            placeholder="Enter antagonist name"
          />
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <label style={{ display: 'block', fontSize: '16px', fontWeight: '500', color: '#495057' }}>
                Want (Tangible Story)
              </label>
             <span 
                onClick={() => setSelectedKnowledge('antagonistWant')}
                style={{ 
                  fontSize: '16px', 
                  cursor: 'pointer', 
                  padding: '2px',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease',
                  transform: 'scale(1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f8f9fa';
                  e.target.style.transform = 'scale(1.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                📖
              </span>
            </div>
            <textarea
              style={{ 
                width: '100%', 
                padding: '12px 16px', 
                border: '2px solid #dee2e6', 
                borderRadius: '8px', 
                height: '120px',
                fontSize: '16px',
                resize: 'vertical',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              value={story.antagonist.goal}
              onChange={(e) => updateStory('antagonist', 'goal', e.target.value)}
              placeholder="What concrete goal puts them in direct conflict with the protagonist?"
            />
          </div>
          
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <label style={{ display: 'block', fontSize: '16px', fontWeight: '500', color: '#495057' }}>
                Belief (Thematic Contrast)
              </label>
              <span 
                onClick={() => setSelectedKnowledge('antagonistBelief')}
                style={{ 
                  fontSize: '16px', 
                  cursor: 'pointer', 
                  padding: '2px',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease',
                  transform: 'scale(1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f8f9fa';
                  e.target.style.transform = 'scale(1.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                📖
              </span>
            </div>
            <textarea
              style={{ 
                width: '100%', 
                padding: '12px 16px', 
                border: '2px solid #dee2e6', 
                borderRadius: '8px', 
                height: '120px',
                fontSize: '16px',
                resize: 'vertical',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              value={story.antagonist.motivation}
              onChange={(e) => updateStory('antagonist', 'motivation', e.target.value)}
              placeholder="What core belief or worldview drives them—and how does it conflict with the protagonist's path of growth?"
            />
          </div>
        </div>
        
        <div style={{ marginTop: '24px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <label style={{ display: 'block', fontSize: '16px', fontWeight: '500', color: '#495057' }}>
                Method (Strategy or Advantage)
              </label>
              <span 
                onClick={() => setSelectedKnowledge('antagonistMethod')}
                style={{ 
                  fontSize: '16px', 
                  cursor: 'pointer', 
                  padding: '2px',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease',
                  transform: 'scale(1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f8f9fa';
                  e.target.style.transform = 'scale(1.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                📖
              </span>
            </div>
            <textarea
              style={{ 
                width: '100%', 
                padding: '12px 16px', 
                border: '2px solid #dee2e6', 
                borderRadius: '8px', 
                height: '120px',
                fontSize: '16px',
                resize: 'vertical',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              value={story.antagonist.methods}
              onChange={(e) => updateStory('antagonist', 'methods', e.target.value)}
              placeholder="How do they pursue their goal—and what edge do they have over the protagonist?"
            />
          </div>
        </div>
      </div>

      {/* Supporting Characters Section */}
      <div style={{
        marginBottom: '32px', backgroundColor: 'white', padding: '32px',
        borderRadius: '16px', boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
        border: '3px solid #1E3A8A'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h3 style={{ fontSize: '28px', fontWeight: '600', margin: 0, color: '#495057' }}>Supporting Characters</h3>
            <span 
            onClick={() => setSelectedKnowledge('secondaryCharacters')}
            style={{ 
              fontSize: '20px', 
              cursor: 'pointer', 
              padding: '4px',
              borderRadius: '4px',
              transition: 'all 0.2s ease',
              transform: 'scale(1)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f8f9fa';
              e.target.style.transform = 'scale(1.15)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.transform = 'scale(1)';
            }}
          >
            📖
          </span>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {/* Show sort filter only when there are 3+ characters */}
            {story.secondaryCharacters.length > 2 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                  Sort by:
                </label>
                <select
  value={sortFilter}
  onChange={(e) => setSortFilter(e.target.value)}
  style={{
    padding: '6px 12px',
    border: '2px solid #dee2e6',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    cursor: 'pointer',
    backgroundColor: 'white',
    minWidth: '140px'
  }}
>
  <option value="creation">Creation Order</option>
  <option value="alphabetical">Alphabetical</option>
  <option value="majorFirst">Major First</option>
  <option value="minorFirst">Minor First</option>
</select>

              </div>
            )}
            <button
              onClick={handleAddCharacter}
              style={{
                padding: '8px 16px',
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#2980b9'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#3498db'}
            >
              Add Character
            </button>
          </div>
        </div>

        {story.secondaryCharacters.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px', 
            color: '#6c757d',
            fontStyle: 'italic',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px'
          }}>
            No supporting characters added yet. Click "Add Character" to start building your cast.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {sortedCharacters.map((character) => (
              <CharacterCard
                key={character.id}
                character={character}
                updateSecondaryCharacter={updateSecondaryCharacter}
                removeSecondaryCharacter={removeSecondaryCharacter}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

function App() {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginPassword, setLoginPassword] = useState('');
  
  const [activeView, setActiveView] = useState('wheel');
  const [selectedBeat, setSelectedBeat] = useState(null);
  const [selectedArc, setSelectedArc] = useState(null);
  const [selectedWantNeed, setSelectedWantNeed] = useState(null);
  const [selectedKnowledge, setSelectedKnowledge] = useState(null);
  const [selectedSequence, setSelectedSequence] = useState(null);
  const [showStoryModal, setShowStoryModal] = useState(false);
  const [showStoryTypeModal, setShowStoryTypeModal] = useState(false);
  const [tempStoryType, setTempStoryType] = useState(null);
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [activeGuideSection, setActiveGuideSection] = useState(0);
  const [wheelView, setWheelView] = useState('sequences'); // 'beats' or 'sequences'
  const [currentAudio, setCurrentAudio] = useState(null);
  
  // Hover state for clickable elements
  const [hoveredElement, setHoveredElement] = useState(null);
  
// Story Type Helper Functions
  const getPriorityMedal = (elementId, elementType = 'beat') => {
    if (!story.storyType) return '';
    
    const storyTypeConfig = STORY_TYPES[story.storyType];
    if (!storyTypeConfig || !storyTypeConfig.beatPriorities) return '';
    
    if (elementType === 'beat') {
      const priority = storyTypeConfig.beatPriorities[elementId];
      switch (priority) {
        case 'essential': return '🥇 ';
        case 'important': return '🥈 ';
        case 'supporting': return '🥉 ';
        default: return '';
      }
    } else if (elementType === 'sequence') {
      const sequenceBeatMapping = {
        'grounding': ['aHero', 'meetAndGreet', 'catalyst'],
        'disruption': ['umAndAh', 'leap'],
        'exploration': ['upAndAtEm', 'complication'],
        'opposition': ['touchAndGo', 'turn'],
        'reckoning': ['downAndDirty', 'risk'],
        'collapse': ['crashAndBurn', 'fall'],
        'reflection': ['riseAndShine', 'reveal'],
        'rebirth': ['triedAndTested', 'theHero']
      };
      
      const sequenceBeats = sequenceBeatMapping[elementId] || [];
      const beatPriorities = sequenceBeats.map(beatId => storyTypeConfig.beatPriorities[beatId]).filter(p => p);
      
      if (beatPriorities.includes('essential')) return '🥇 ';
      if (beatPriorities.includes('important')) return '🥈 ';
      return '🥉 ';
    }
    return '';
  };

  const renderPriorityLegend = () => {
    if (!story.storyType) return null;
    
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        fontSize: '14px',
        color: '#666',
        fontWeight: '500'
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ color: '#FFD700' }}>🥇</span>
          Essential
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ color: '#C0C0C0' }}>🥈</span>
          Important
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ color: '#CD7F32' }}>🥉</span>
          Supporting
        </span>
      </div>
    );
  };

  
  
  const selectStoryType = (storyTypeId) => {
    setStory(prevStory => ({
      ...prevStory,
      storyType: prevStory.storyType === storyTypeId ? null : storyTypeId
    }));
  };
  
  const closeStoryTypeModal = () => {
    setShowStoryTypeModal(false);
  };
  
 const openStoryTypeModal = () => {
  setTempStoryType(story.storyType); // Initialize with current selection
  setShowStoryTypeModal(true);
};

// ===== SEQUENCE HELPER FUNCTIONS =====
const getBeatPrompt = (beatId = 'aHero') => {
  console.log('getBeatPrompt called with:', beatId);
  console.log('story.storyType:', story.storyType);
  console.log('STORY_TYPES available:', !!STORY_TYPES);
  if (!story.storyType || !STORY_TYPES[story.storyType]) {
    return beatDetails[beatId]?.description || `Write your notes for ${beatDetails[beatId]?.name || 'this beat'}...`;
  }
  
  const storyTypeConfig = STORY_TYPES[story.storyType];
  const customPrompt = storyTypeConfig.promptOverrides?.[beatId];
  
  if (customPrompt) {
    return customPrompt;
  }
  
  return beatDetails[beatId]?.description || `Write your notes for ${beatDetails[beatId]?.name || 'this beat'}...`;
};

const getSequencePrompt = (sequenceId) => {
  console.log('FUNCTION CALLED!');
  console.log('Debug:', sequenceId, story.storyType, STORY_TYPES[story.storyType]);
  if (!story.storyType || !STORY_TYPES[story.storyType] || story.storyType === 'undecided') {
    return null;
  }
  
  const storyTypeConfig = STORY_TYPES[story.storyType];
  const customPrompt = storyTypeConfig.sequencePrompts?.[sequenceId];
  
  if (customPrompt) {
    return customPrompt;
  }
  
  // Fallback to generic prompt
  const sequenceNames = {
    grounding: 'Grounding',
    disruption: 'Disruption', 
    exploration: 'Exploration',
    opposition: 'Opposition',
    reckoning: 'Reckoning',
    collapse: 'Collapse',
    reflection: 'Reflection',
    rebirth: 'Rebirth'
  };
  return `Write your notes for the ${sequenceNames[sequenceId]} sequence...`;
};

const getArcPrompt = (arcId) => {
  if (!story.storyType || !STORY_TYPES[story.storyType] || story.storyType === 'undecided') {
    return null;
  }
  
  const storyTypeConfig = STORY_TYPES[story.storyType];
  const customPrompt = storyTypeConfig.arcPrompts?.[arcId];
  
  if (customPrompt) {
    return customPrompt;
  }
  
  // Fallback to generic prompt
  const arcNames = {
    setUp: 'Set Up',
    buildUp: 'Build Up',
    breakUp: 'Break Up', 
    wrapUp: 'Wrap Up'
  };
  return `Write your notes for the ${arcNames[arcId]} arc of your story...`;
};

const getBeatDescription = (beatId) => {
  if (!story.storyType || !STORY_TYPES[story.storyType]) {
    return beatDetails[beatId]?.description || '';
  }
  
  const storyTypeDescriptions = {
    growingPains: {
      'aHero': 'Blissfully unaware of what\'s coming',
      'meetAndGreet': 'The people who\'ll change everything',
      'catalyst': 'Reality comes knocking',
      'umAndAh': 'Desperately clinging to childhood',
      'leap': 'Stepping into the unknown',
      'upAndAtEm': 'First attempts at being grown-up',
      'complication': 'Growing up is harder than expected',
      'touchAndGo': 'Am I doing this right?',
      'turn': 'The moment everything clicks',
      'downAndDirty': 'Wrestling with who they really are',
      'risk': 'What they\'ll sacrifice to grow',
      'crashAndBurn': 'Epic failure at adulting',
      'fall': 'Lost and completely confused',
      'riseAndShine': 'Finding their authentic voice',
      'reveal': 'The truth about growing up',
      'triedAndTested': 'Proving they\'ve actually changed',
      'theHero': 'Welcome to adulthood'
    },
    theLongRoad: {
      'aHero': 'Comfortable life before adventure',
      'meetAndGreet': 'Future companions and mentors',
      'catalyst': 'The mission that changes everything',
      'umAndAh': 'Thanks but no thanks',
      'leap': 'Fine, I\'ll save the world',
      'upAndAtEm': 'Early wins build confidence',
      'complication': 'This is way harder than expected',
      'touchAndGo': 'Maybe we should just go home',
      'turn': 'Game-changing revelation',
      'downAndDirty': 'Doing whatever it takes',
      'risk': 'Betting everything on success',
      'crashAndBurn': 'When plans go spectacularly wrong',
      'fall': 'All hope seems lost',
      'riseAndShine': 'Finding strength they didn\'t know they had',
      'reveal': 'The truth behind the quest',
      'triedAndTested': 'The ultimate test',
      'theHero': 'Forever changed by the journey'
    },
    mattersOfTheHeart: {
      'aHero': 'Life before love complicated things',
      'meetAndGreet': 'When eyes meet across the room',
      'catalyst': 'The spark that ignites everything',
      'umAndAh': 'Playing hard to get',
      'leap': 'Taking a chance on love',
      'upAndAtEm': 'The honeymoon phase',
      'complication': 'Love hits its first roadblock',
      'touchAndGo': 'Are we really right for each other?',
      'turn': 'The relationship shifts',
      'downAndDirty': 'Fighting for what they have',
      'risk': 'What they\'ll sacrifice for love',
      'crashAndBurn': 'Everything falls apart',
      'fall': 'Love seems impossible',
      'riseAndShine': 'Understanding what love really means',
      'reveal': 'The truth that changes everything',
      'triedAndTested': 'Love\'s final test',
      'theHero': 'Finding where they belong'
    },
    eyeForAnEye: {
      'aHero': 'Life before everything went wrong',
      'meetAndGreet': 'Meeting future allies and targets',
      'catalyst': 'The betrayal that changes everything',
      'umAndAh': 'Wrestling with going dark',
      'leap': 'Choosing the path of vengeance',
      'upAndAtEm': 'First taste of payback',
      'complication': 'Revenge gets messy',
      'touchAndGo': 'Questioning if it\'s worth it',
      'turn': 'Revenge escalates beyond control',
      'downAndDirty': 'Getting hands really dirty',
      'risk': 'Betting everything on justice',
      'crashAndBurn': 'When revenge backfires',
      'fall': 'Rock bottom moment',
      'riseAndShine': 'Finding final resolve',
      'reveal': 'Truth about what justice costs',
      'triedAndTested': 'The final reckoning',
      'theHero': 'What revenge made them'
    },
    secondChances: {
      'aHero': 'Rock bottom and hating themselves',
      'meetAndGreet': 'People they\'ve hurt or who care',
      'catalyst': 'The wake-up call they can\'t ignore',
      'umAndAh': 'But change is really hard',
      'leap': 'Committing to do better',
      'upAndAtEm': 'Baby steps toward redemption',
      'complication': 'Past keeps getting in the way',
      'touchAndGo': 'Do they even deserve forgiveness?',
      'turn': 'Understanding what they really lost',
      'downAndDirty': 'Doing the hard work of change',
      'risk': 'What they\'ll give up to make amends',
      'crashAndBurn': 'Backsliding into old ways',
      'fall': 'Facing the full weight of their actions',
      'riseAndShine': 'Finding the strength to keep trying',
      'reveal': 'What redemption really requires',
      'triedAndTested': 'Proving they\'ve truly changed',
      'theHero': 'Earning their second chance'
    },
    fishOutOfWater: {
      'aHero': 'Comfortable in their old world',
      'meetAndGreet': 'First awkward encounters',
      'catalyst': 'Thrown into the deep end',
      'umAndAh': 'This place makes no sense',
      'leap': 'Deciding to try fitting in',
      'upAndAtEm': 'First successful adaptation',
      'complication': 'Culture clash goes badly',
      'touchAndGo': 'Am I losing who I really am?',
      'turn': 'Finding their place',
      'downAndDirty': 'Desperately trying to belong',
      'risk': 'What they\'ll sacrifice to fit in',
      'crashAndBurn': 'Epic cultural misunderstanding',
      'fall': 'Completely rejected and alone',
      'riseAndShine': 'Realizing they don\'t have to choose',
      'reveal': 'Truth about identity and belonging',
      'triedAndTested': 'Final test of who they are',
      'theHero': 'Bridging both worlds'
    },
    davidVsGoliath: {
      'aHero': 'Small person in a big system',
      'meetAndGreet': 'Meeting the unstoppable force',
      'catalyst': 'When the system crushes something precious',
      'umAndAh': 'Who am I to fight this?',
      'leap': 'Standing up anyway',
      'upAndAtEm': 'First small victory',
      'complication': 'System fights back hard',
      'touchAndGo': 'Maybe this was a mistake',
      'turn': 'Finding their secret weapon',
      'downAndDirty': 'Using every trick they have',
      'risk': 'Going all in',
      'crashAndBurn': 'System seems to win',
      'fall': 'Crushed by overwhelming power',
      'riseAndShine': 'One last shot at victory',
      'reveal': 'Truth about power and courage',
      'triedAndTested': 'David meets Goliath',
      'theHero': 'Giant slayer'
    },
    fallFromGrace: {
      'aHero': 'Top of the world',
      'meetAndGreet': 'People who\'ll watch them fall',
      'catalyst': 'First step down the dark path',
      'umAndAh': 'Moral hesitation',
      'leap': 'Crossing the line',
      'upAndAtEm': 'Success through compromise',
      'complication': 'Consequences start catching up',
      'touchAndGo': 'Moment of moral doubt',
      'turn': 'Point of no return',
      'downAndDirty': 'Deeper into corruption',
      'risk': 'Desperate measures',
      'crashAndBurn': 'Everything unravels',
      'fall': 'Complete destruction',
      'riseAndShine': 'Too late for redemption',
      'reveal': 'Truth finally exposed',
      'triedAndTested': 'Facing the consequences',
      'theHero': 'What they\'ve become'
    },
    mystery: {
      'aHero': 'Normal life before the case',
      'meetAndGreet': 'Suspects, witnesses, and allies',
      'catalyst': 'The mystery that needs solving',
      'umAndAh': 'Should I get involved?',
      'leap': 'Taking the case',
      'upAndAtEm': 'First breakthrough',
      'complication': 'Trail goes cold',
      'touchAndGo': 'Doubting their theory',
      'turn': 'Game-changing revelation',
      'downAndDirty': 'Getting dangerously close',
      'risk': 'What they\'ll risk for truth',
      'crashAndBurn': 'False lead or betrayal',
      'fall': 'Completely lost',
      'riseAndShine': 'Finally connecting the dots',
      'reveal': 'The shocking truth',
      'triedAndTested': 'Confronting the real culprit',
      'theHero': 'Justice served'
    },
    underSiege: {
      'aHero': 'Life before everything went sideways',
      'meetAndGreet': 'People they\'ll need to keep alive',
      'catalyst': 'When all hell breaks loose',
      'umAndAh': 'This can\'t be happening',
      'leap': 'Time to fight or die',
      'upAndAtEm': 'First successful counter-attack',
      'complication': 'Threat is worse than imagined',
      'touchAndGo': 'Are we all going to die?',
      'turn': 'Understanding what they\'re up against',
      'downAndDirty': 'Doing whatever it takes to survive',
      'risk': 'Life-or-death gamble',
      'crashAndBurn': 'When the plan goes to hell',
      'fall': 'All hope seems lost',
      'riseAndShine': 'Finding unexpected strength',
      'reveal': 'Key to beating the threat',
      'triedAndTested': 'Final desperate stand',
      'theHero': 'Survivors forever changed'
    },
    makeAWish: {
      'aHero': 'Life before magic changed everything',
      'meetAndGreet': 'The people in their ordinary world',
      'catalyst': 'The wish is granted',
      'umAndAh': 'This is too good to be true',
      'leap': 'Embracing the magical new reality',
      'upAndAtEm': 'Enjoying the perfect life',
      'complication': 'Unintended consequences emerge',
      'touchAndGo': 'Is this worth the hidden cost?',
      'turn': 'Understanding the true price',
      'downAndDirty': 'Trying to fix the magical mess',
      'risk': 'What they\'ll sacrifice to make it right',
      'crashAndBurn': 'When magic backfires completely',
      'fall': 'Trapped by their own wish',
      'riseAndShine': 'Finding wisdom in the chaos',
      'reveal': 'What they actually needed all along',
      'triedAndTested': 'Choosing reality over fantasy',
      'theHero': 'Wiser about what really matters'
    },
    ragsToRiches: {
      'aHero': 'Starting from the bottom',
      'meetAndGreet': 'People from both worlds they\'ll navigate',
      'catalyst': 'The opportunity to rise',
      'umAndAh': 'Do I really belong up there?',
      'leap': 'Going for the higher life',
      'upAndAtEm': 'First taste of success',
      'complication': 'The climb gets complicated',
      'touchAndGo': 'Am I losing who I really am?',
      'turn': 'Understanding what success costs',
      'downAndDirty': 'Doing whatever it takes to make it',
      'risk': 'What they\'ll sacrifice for status',
      'crashAndBurn': 'When ambition backfires',
      'fall': 'Losing everything they worked for',
      'riseAndShine': 'Finding their authentic worth',
      'reveal': 'What true success really means',
      'triedAndTested': 'Proving they deserve their place',
      'theHero': 'Rich in what actually matters'
    },
    toTheRescue: {
      'aHero': 'Life before everything went wrong',
      'meetAndGreet': 'The precious person and potential allies',
      'catalyst': 'Someone precious is in danger',
      'umAndAh': 'This might be impossible',
      'leap': 'I\'m going to save them',
      'upAndAtEm': 'First progress toward the rescue',
      'complication': 'Obstacles are worse than expected',
      'touchAndGo': 'Maybe I can\'t do this',
      'turn': 'Understanding what it really takes',
      'downAndDirty': 'Going to any lengths necessary',
      'risk': 'What they\'ll sacrifice to save them',
      'crashAndBurn': 'When the rescue plan fails',
      'fall': 'All hope of saving them seems lost',
      'riseAndShine': 'Finding unexpected strength',
      'reveal': 'The key to the rescue',
      'triedAndTested': 'The final desperate attempt',
      'theHero': 'Hero forged by love and sacrifice'
    }
  };
  
  const descriptions = storyTypeDescriptions[story.storyType];
  return descriptions?.[beatId] || beatDetails[beatId]?.description || '';
};


// ===== MAIN COMPONENT LOGIC =====
const [story, setStory] = useState(() => {
  // Initialize story beats on startup
  const beats = {};
  const beatNames = [
    'aHero', 'meetAndGreet', 'catalyst', 'umAndAh', 'leap',
    'upAndAtEm', 'complication', 'touchAndGo', 'turn',
    'downAndDirty', 'risk', 'crashAndBurn', 'fall',
    'riseAndShine', 'reveal', 'triedAndTested', 'theHero'
  ];
  
  beatNames.forEach(beat => {
    beats[beat] = { notes: '', completed: false };
  });

  return {
    title: '',
    storyType: null,
    storyTypeConfig: null, 
    protagonist: { name: '', motivation: '', want: '', desire: '', need: '' },
    antagonist: { name: '', motivation: '', goal: '', methods: '', relationship: '' },
    secondaryCharacters: [],
    miniArcs: {
      setUp: { notes: '', completed: false },
      buildUp: { notes: '', completed: false },
      breakUp: { notes: '', completed: false },
      wrapUp: { notes: '', completed: false }
    },
    storyBeats: beats,
    storySequences: {
      grounding: { notes: '', completed: false },
      disruption: { notes: '', completed: false },
      exploration: { notes: '', completed: false },
      opposition: { notes: '', completed: false },
      reckoning: { notes: '', completed: false },
      collapse: { notes: '', completed: false },
      reflection: { notes: '', completed: false },
      rebirth: { notes: '', completed: false }
    },
    theme: '',
    themePremise: '',
    dilemma: '',
    theLie: '',
    theTruth: '',
    moralStakes: '',
    tangibleStory: '',
    thematicStory: ''
  };
});

// ===== SEQUENCE CONFIGURATION =====
// Sequence data now imported from data/sequenceDetails.js

  // Beat details with descriptions and prompts
  

  // Mini-arc details with descriptions
  

  // Want vs Need explanations for each arc
  const wantNeedExplanations = {
    setUp: {
  title: "Set Up - WANT ≠ NEED",
  content: (
    <div>
      <h4 style={{ color: '#4A90E2', marginTop: '20px', marginBottom: '12px' }}>What It Looks Like:</h4>
      <p>At the start, the protagonist is fully committed to their Want—an external, clear goal they think will fix their life, earn approval, or give them control. The Need—the emotional truth they must face—is buried. Maybe they're avoiding it. Maybe they don't even know it's there. But it's already shaping their behavior.</p>
      
      <h4 style={{ color: '#4A90E2', marginTop: '20px', marginBottom: '12px' }}>Why It Matters:</h4>
      <p>This imbalance sets the foundation for your story. The Want drives the action, while the Need quietly drives the character. It's important the audience sees what the protagonist wants and starts to sense what might be missing underneath.</p>
      
      <h4 style={{ color: '#4A90E2', marginTop: '20px', marginBottom: '12px' }}>Narrative Insight:</h4>
      <p>The audience often understands the Need before the character does. That's where the tension begins—we see the gap between what they're chasing and what they actually need.</p>
    </div>
  ),
  color: "#4A90E2"
},
    buildUp: {
  title: "Build Up - WANT > NEED",
  content: (
    <div>
      <h4 style={{ color: '#7ED321', marginTop: '20px', marginBottom: '12px' }}>What It Looks Like:</h4>
      <p>The Want gains traction or runs into trouble. The protagonist takes bigger steps toward their goal. But challenges start poking at their core beliefs, values, or fears. The Need begins to show up—through conflict, relationships, or mistakes that repeat. They might not recognize it yet, but it's there, gaining pressure.</p>
      
      <h4 style={{ color: '#7ED321', marginTop: '20px', marginBottom: '12px' }}>Why It Matters:</h4>
      <p>This is where the real push-and-pull begins. The story doesn't just escalate in plot—it escalates emotionally. Every action the protagonist takes brings them closer to a personal crossroads.</p>
      
      <h4 style={{ color: '#7ED321', marginTop: '20px', marginBottom: '12px' }}>Narrative Insight:</h4>
      <p>Supporting characters are often the ones holding up the mirror here—some defending the Want, others quietly pointing to the Need. The contrast sharpens.</p>
    </div>
  ),
  color: "#7ED321"
},
    breakUp: {
  title: "Break Up - NEED > WANT",
  content: (
    <div>
      <h4 style={{ color: '#e42e54', marginTop: '20px', marginBottom: '12px' }}>What It Looks Like:</h4>
      <p>The story—and the protagonist—hits a breaking point. The Want either fails, backfires, or is exposed as not enough. The Need can no longer be ignored. This is where identity cracks, beliefs are shaken, and choices really matter.</p>
      
      <h4 style={{ color: '#e42e54', marginTop: '20px', marginBottom: '12px' }}>Why It Matters:</h4>
      <p>This is the emotional heart of the story. The character might feel like they've lost everything—but they're closer than ever to the truth. Whether they resist or accept it, this is the turning point.</p>
      
      <h4 style={{ color: '#e42e54', marginTop: '20px', marginBottom: '12px' }}>Narrative Insight:</h4>
      <p>What breaks here is not just the plan—it's the illusion that the Want alone is enough. If the character doubles down, they may collapse. If they face the Need, they open the door to transformation.</p>
    </div>
  ),
  color: "#e42e54"
},
    wrapUp: {
  title: "Wrap Up - NEED = WANT",
  content: (
    <div>
      <h4 style={{ color: '#a85cd9', marginTop: '20px', marginBottom: '12px' }}>What It Looks Like:</h4>
      <p>The story resolves based on the character's choice: do they meet their Need or cling to their Want? Sometimes the Want is achieved, but it's no longer the point. Sometimes it's sacrificed. What matters is how the character ends up emotionally.</p>
      
      <h4 style={{ color: '#a85cd9', marginTop: '20px', marginBottom: '12px' }}>Why It Matters:</h4>
      <p>This is the payoff—for the character and the audience. A satisfying ending isn't just about who wins or loses—it's about whether the character became whole, even if the world around them didn't.</p>
      
      <h4 style={{ color: '#a85cd9', marginTop: '20px', marginBottom: '12px' }}>Narrative Insight:</h4>
      <p>When the Want and Need finally align, the world feels right-side up again. That's why this story needed to be told now—because this is the moment the protagonist was finally ready to change - or learned they couldn't.</p>
    </div>
  ),
  color: "#a85cd9"
}
  };

  // Individual beat colors for beats with '&' - matching their pie slice colors
const individualBeatColors = {
  meetAndGreet: { color: '#42A5F5', lightColor: '#E3F2FD' },
  umAndAh: { color: '#1E88E5', lightColor: '#E3F2FD' },
  upAndAtEm: { color: '#9CCC65', lightColor: '#E8F5E8' },
  touchAndGo: { color: '#FFEB3B', lightColor: '#FFFDE7' },
  downAndDirty: { color: '#fea02e', lightColor: '#FFF3E0' },
  crashAndBurn: { color: '#D32F2F', lightColor: '#FFEBEE' },
  riseAndShine: { color: '#EC407A', lightColor: '#FCE4EC' },
  triedAndTested: { color: '#a85cd9', lightColor: '#f0e7f7' },
  // Turning point beats in copper/bronze
  aHero: { color: '#CD7F32', lightColor: '#F5F0E8' },
  catalyst: { color: '#3097ED', lightColor: '#E3F2FD' },
  leap: { color: '#5DAAA5', lightColor: '#E8F5F5' },
  complication: { color: '#CEDC50', lightColor: '#F5F8E8' },
  turn: { color: '#F28D48', lightColor: '#FFF3E8' },
  risk: { color: '#E74C3C', lightColor: '#FADBD8' },
  fall: { color: '#E03855', lightColor: '#FADDE1' },
  reveal: { color: '#CA4EAA', lightColor: '#F8E5F5' },
  theHero: { color: '#DAA520', lightColor: '#FDF6E3' },
};

  // Login handler
  const handleLogin = (e) => {
    e.preventDefault();
    if (loginPassword === 'StoryBeta2024') {
      setIsAuthenticated(true);
      setLoginPassword('');
    } else {
      alert('Incorrect password. Please try again.');
      setLoginPassword('');
    }
  };

  // Initialize story beats
  useEffect(() => {
    const beats = {};
    const beatNames = [
      'aHero', 'meetAndGreet', 'catalyst', 'umAndAh', 'leap',
      'upAndAtEm', 'complication', 'touchAndGo', 'turn',
      'downAndDirty', 'risk', 'crashAndBurn', 'fall',
      'riseAndShine', 'reveal', 'triedAndTested', 'theHero'
    ];
    
    beatNames.forEach(beat => {
      beats[beat] = { notes: '', completed: false };
    });
    
    setStory(prevStory => ({
      ...prevStory,
      storyBeats: beats
    }));
  }, []);

  // Reset guide section when modal closes and add scroll spy
  useEffect(() => {
    if (!showGuideModal) {
      setActiveGuideSection(0);
      return;
    }

    let scrollContainer = null;

    const handleScroll = () => {
      const sections = [];
      
      // Collect all sections with their positions
      for (let i = 0; i < 8; i++) {
        const section = document.getElementById(`section-${i}`);
        if (section && scrollContainer) {
          const containerRect = scrollContainer.getBoundingClientRect();
          const sectionRect = section.getBoundingClientRect();
          
          // Calculate position relative to container
          const relativeTop = sectionRect.top - containerRect.top;
          const relativeBottom = sectionRect.bottom - containerRect.top;
          
          sections.push({
            index: i,
            top: relativeTop,
            bottom: relativeBottom
          });
        }
      }
      
      // Find which section is most visible (closest to top of container)
      let activeIndex = 0;
      const threshold = 100; // Section becomes active when it's 100px from top
      
      for (const section of sections) {
        if (section.top <= threshold) {
          activeIndex = section.index;
        } else {
          break;
        }
      }
      
      setActiveGuideSection(activeIndex);
    };

    // Find and setup scroll container
    const setupScrollSpy = () => {
      scrollContainer = document.querySelector('.guide-content-area');
      if (!scrollContainer) {
        // Fallback: find by style attributes
        const containers = document.querySelectorAll('[style*="overflow: auto"]');
        for (const container of containers) {
          if (container.querySelector('#section-0')) {
            scrollContainer = container;
            break;
          }
        }
      }
      
      if (scrollContainer) {
        scrollContainer.addEventListener('scroll', handleScroll);
        handleScroll(); // Call once to set initial state
      }
    };

    // Setup with delay to ensure DOM is ready
    const timeoutId = setTimeout(setupScrollSpy, 200);

    return () => {
      clearTimeout(timeoutId);
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };

  }, [showGuideModal]);

  const updateStory = (section, field, value) => {
    setStory(prevStory => {
      if (section) {
        return {
          ...prevStory,
          [section]: {
            ...prevStory[section],
            [field]: value
          }
        };
      } else {
        return {
          ...prevStory,
          [field]: value
        };
      }
    });
  };

  // Story Type Selection Functions

const clearStoryType = () => {
  setStory(prevStory => ({
    ...prevStory,
    storyType: null,
    storyTypeConfig: null
  }));
};

  const updateArc = (arcId, field, value) => {
    setStory(prevStory => ({
      ...prevStory,
      miniArcs: {
        ...prevStory.miniArcs,
        [arcId]: {
          ...prevStory.miniArcs[arcId],
          [field]: value
        }
      }
    }));
  };

  const updateSequence = (sequenceId, field, value) => {
    setStory(prevStory => ({
      ...prevStory,
      storySequences: {
        ...prevStory.storySequences,
        [sequenceId]: {
          ...prevStory.storySequences[sequenceId],
          [field]: value
        }
      }
    }));
  };

  const updateStoryBeat = (beatId, field, value) => {
    setStory(prevStory => ({
      ...prevStory,
      storyBeats: {
        ...prevStory.storyBeats,
        [beatId]: {
          ...prevStory.storyBeats[beatId],
          [field]: value
        }
      }
    }));
  };

  const addSecondaryCharacter = (tangibleRole = '', thematicRole = '') => {
    const newCharacter = {
  id: Date.now().toString(),
  name: '',
  tangibleRole: tangibleRole,
  thematicRole: thematicRole,
  description: '',
  relationshipToProtagonist: '',
  storyFunction: '',
  thematicFunction: '',
  major: null, // Start as neutral
  isNew: true // Flag for new characters
};
    
    setStory(prevStory => ({
    ...prevStory,
    secondaryCharacters: [newCharacter, ...prevStory.secondaryCharacters]
  }));
  };

  const updateSecondaryCharacter = (characterId, field, value) => {
  setStory(prevStory => ({
    ...prevStory,
    secondaryCharacters: prevStory.secondaryCharacters.map(char =>
      char.id === characterId ? { 
        ...char, 
        [field]: value,
        isNew: false // Remove isNew flag on any interaction
      } : char
    )
  }));
};

  const removeSecondaryCharacter = (characterId) => {
    setStory(prevStory => ({
      ...prevStory,
      secondaryCharacters: prevStory.secondaryCharacters.filter(char => char.id !== characterId)
    }));
  };

  const saveStory = () => {
    const storyData = {
      ...story,
      savedDate: new Date().toISOString(),
      appVersion: "2.0"
    };
    
    const dataStr = JSON.stringify(storyData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    const link = document.createElement('a');
    link.href = URL.createObjectURL(dataBlob);
    link.download = (story.title || 'My Story') + '_' + new Date().toISOString().split('T')[0] + '.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const loadStory = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const loadedStory = JSON.parse(e.target.result);
        setStory({
          title: loadedStory.title || '',
          storyType: loadedStory.storyType || null,
          protagonist: loadedStory.protagonist || {
            name: '', motivation: '', want: '', desire: '', need: ''
          },
          antagonist: loadedStory.antagonist || {
            name: '', motivation: '', goal: '', methods: '', relationship: ''
          },
          secondaryCharacters: (loadedStory.secondaryCharacters || []).map(char => ({
            ...char,
            major: char.major !== undefined ? char.major : null,
            tangibleRole: char.tangibleRole || '',
            thematicRole: char.thematicRole || ''
          })),
          miniArcs: loadedStory.miniArcs || {
            setUp: { notes: '', completed: false }, 
            buildUp: { notes: '', completed: false },
            breakUp: { notes: '', completed: false }, 
            wrapUp: { notes: '', completed: false }
          },
          storyBeats: loadedStory.storyBeats || {},
          storySequences: loadedStory.storySequences || {
            grounding: { notes: '', completed: false },
            disruption: { notes: '', completed: false },
            exploration: { notes: '', completed: false },
            opposition: { notes: '', completed: false },
            reckoning: { notes: '', completed: false },
            collapse: { notes: '', completed: false },
            reflection: { notes: '', completed: false },
            rebirth: { notes: '', completed: false }
          },
          theme: loadedStory.theme || '',
          themePremise: loadedStory.themePremise || '',
          dilemma: loadedStory.dilemma || '',
          theLie: loadedStory.theLie || '',
          theTruth: loadedStory.theTruth || '',
          moralStakes: loadedStory.moralStakes || '',
          tangibleStory: loadedStory.tangibleStory || '',
          thematicStory: loadedStory.thematicStory || ''
        });
        alert('Story loaded successfully!');
      } catch (error) {
        alert('Error loading story file.');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const startNewProject = () => {
    if (window.confirm('Are you sure you want to start a new project? Any unsaved changes will be lost.')) {
      setStory({
        title: '',
        storyType: null,
        protagonist: { name: '', motivation: '', want: '', desire: '', need: '' },
        antagonist: { name: '', motivation: '', goal: '', methods: '', relationship: '' },
        secondaryCharacters: [],
        miniArcs: {
          setUp: { notes: '', completed: false },
          buildUp: { notes: '', completed: false },
          breakUp: { notes: '', completed: false },
          wrapUp: { notes: '', completed: false }
        },
        storyBeats: {},
        storySequences: {
          grounding: { notes: '', completed: false },
          disruption: { notes: '', completed: false },
          exploration: { notes: '', completed: false },
          opposition: { notes: '', completed: false },
          reckoning: { notes: '', completed: false },
          collapse: { notes: '', completed: false },
          reflection: { notes: '', completed: false },
          rebirth: { notes: '', completed: false }
        },
        theme: '',
        themePremise: '',
        dilemma: '',
        theLie: '',
        theTruth: '',
        moralStakes: '',
        tangibleStory: '',
        thematicStory: ''
      });
      
      const beats = {};
      const beatNames = [
        'aHero', 'meetAndGreet', 'catalyst', 'umAndAh', 'leap',
        'upAndAtEm', 'complication', 'touchAndGo', 'turn',
        'downAndDirty', 'risk', 'crashAndBurn', 'fall',
        'riseAndShine', 'reveal', 'triedAndTested', 'theHero'
      ];
      
      beatNames.forEach(beat => {
        beats[beat] = { notes: '', completed: false };
      });
      
      setStory(prevStory => ({
        ...prevStory,
        storyBeats: beats
      }));
      
      alert('New project started!');
    }
  };

  const printStory = () => {
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${story.title || 'My Story'} - Story Plan</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; color: black; }
          h1, h2, h3 { color: black; margin-top: 30px; }
          .section { margin-bottom: 30px; page-break-inside: avoid; }
          .character-section { padding: 15px; margin-bottom: 20px; }
          .setup-section { padding: 15px; margin-bottom: 20px; }
          .buildup-section { padding: 15px; margin-bottom: 20px; }
          .breakup-section { padding: 15px; margin-bottom: 20px; }
          .wrapup-section { padding: 15px; margin-bottom: 20px; }
          .storybeats-section { padding: 15px; margin-bottom: 20px; }
          .theme-section { padding: 15px; margin-bottom: 20px; }
          .beat { margin-bottom: 15px; padding: 10px; }
          .completed { }
          @media print {
            body { margin: 0; }
            .section { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <h1>${story.title || 'My Story'}</h1>
        <p><strong>Generated on:</strong> ${new Date().toLocaleDateString()}</p>
        
        <div class="section">
          <h2>Story Overview</h2>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
            <div style="padding: 20px; border-radius: 8px;">
              <h3 style="color: black;">Tangible Story</h3>
              <p>${story.tangibleStory || 'Not defined yet'}</p>
            </div>
            <div style="padding: 20px; border-radius: 8px;">
              <h3 style="color: black;">Thematic Story</h3>
              <p>${story.thematicStory || 'Not defined yet'}</p>
            </div>
          </div>
        </div>
        
        <div class="section theme-section">
          <h2>Theme Development</h2>
          <h3>Theme Premise</h3>
          <p>${story.themePremise || 'Not defined yet'}</p>
        </div>
        
        <div class="section character-section">
          <h2>Character Development</h2>
          <div class="character-section">
            <h3>Protagonist: ${story.protagonist.name || 'Not named'}</h3>
            <p><strong>Want (Tangible Story):</strong><br>${story.protagonist.want || 'Not defined'}</p>
            <p><strong>Need (Thematic Story):</strong><br>${story.protagonist.need || 'Not defined'}</p>
            <p><strong>Motivation (Reason for actions and/or beliefs):</strong><br>${story.protagonist.motivation || 'Not defined'}</p>
            <p><strong>Desire (Innermost hope or wish):</strong><br>${story.protagonist.desire || 'Not defined'}</p>
          </div>
          <div class="character-section">
            <h3>Antagonist: ${story.antagonist.name || 'Not named'}</h3>
            <p><strong>Want (Tangible Story):</strong><br>${story.antagonist.goal || 'Not defined'}</p>
            <p><strong>Belief (Thematic Contrast):</strong><br>${story.antagonist.motivation || 'Not defined'}</p>
            <p><strong>Method (Strategy or Advantage):</strong><br>${story.antagonist.methods || 'Not defined'}</p>
          </div>
          ${story.secondaryCharacters.length > 0 ? `
          <div class="character-section">
            <h3>Secondary Characters</h3>
            ${story.secondaryCharacters.map(char => `
              <div style="margin-bottom: 20px; padding: 15px; border-radius: 8px;">
                <h4>${char.name || 'Unnamed'} - ${char.tangibleRole || 'No role'} ${char.major === true ? '(Major)' : char.major === false ? '(Minor)' : '(Draft)'}</h4>
                <p><strong>Description:</strong><br>${char.description || 'No description'}</p>
                <p><strong>Relationship to Protagonist:</strong><br>${char.relationshipToProtagonist || 'Not defined'}</p>
                <p><strong>Story Function:</strong><br>${char.storyFunction || 'Not defined'}</p>
                <p><strong>Thematic Function:</strong><br>${char.thematicFunction || 'Not defined'}</p>
              </div>
            `).join('')}
          </div>
          ` : ''}
        </div>
        
        <div class="section">
          <h2>StoryArcs</h2>
          <div class="setup-section">
          <h3>Set Up (Introduction) ${story.miniArcs.setUp?.completed ? '✓' : ''}</h3>
          <p>${story.miniArcs.setUp.notes || 'No notes yet'}</p>
        </div>
          
          <div class="buildup-section">
          <h3>Build Up (Escalation) ${story.miniArcs.buildUp?.completed ? '✓' : ''}</h3>
          <p>${story.miniArcs.buildUp.notes || 'No notes yet'}</p>
        </div>
          
          <div class="breakup-section">
          <h3>Break Up (Dissolution) ${story.miniArcs.breakUp?.completed ? '✓' : ''}</h3>
          <p>${story.miniArcs.breakUp.notes || 'No notes yet'}</p>
        </div>
          
          <div class="wrapup-section">
          <h3>Wrap Up (Resolution) ${story.miniArcs.wrapUp?.completed ? '✓' : ''}</h3>
          <p>${story.miniArcs.wrapUp.notes || 'No notes yet'}</p>
        </div>
        </div>
        
        <div class="section storybeats-section">
          <h2>StoryBeats</h2>
          ${Object.entries(story.storyBeats).map(([beatId, beat]) => {
            const beatNames = {
              aHero: "A Hero", meetAndGreet: "Meet & Greet", catalyst: "The Push",
              umAndAh: "Um & Ah", leap: "The Leap", upAndAtEm: "Up & At 'Em",
              complication: "The Complication", touchAndGo: "Touch & Go", turn: "The Turn",
              downAndDirty: "Down & Dirty", risk: "The Risk", crashAndBurn: "Crash & Burn",
              fall: "The Fall", riseAndShine: "Rise & Shine", reveal: "The Reveal",
              triedAndTested: "Tried & Tested", theHero: "The Hero"
            };
            return `<div class="beat ${beat.completed ? 'completed' : ''}">
              <h4>${beatNames[beatId]} ${beat.completed ? '✓' : ''}</h4>
              <p>${beat.notes || 'No notes yet'}</p>
            </div>`;
          }).join('')}
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  // Common hover style for clickable elements
  const getHoverStyle = (elementId) => ({
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    transform: hoveredElement === elementId ? 'scale(1.05)' : 'scale(1)',
    transformOrigin: 'center center',
    transformBox: 'fill-box'
  });

  // Helper function to split long text into multiple lines
  const renderSequenceText = (text, x) => {
    // Helper to render text with italic parts marked by *text*
    const renderTextWithItalics = (str, key = 0) => {
      if (!str.includes('*')) {
        return str;
      }
      
      const parts = str.split('*');
      return parts.map((part, index) => {
        if (index % 2 === 1) {
          // Odd indices are italic text
          return <tspan key={`${key}-${index}`} fontStyle="italic">{part}</tspan>;
        }
        return part;
      });
    };
    
    // Check if text contains manual line breaks (using | as separator)
    if (text.includes('|')) {
      const lines = text.split('|');
      const lineSpacing = lines.length === 2 ? 0.6 : 0.8;
      const startOffset = lines.length === 2 ? -0.3 : -0.6;
      
      return (
        <>
          {lines.map((line, index) => (
            <tspan key={index} x={x} dy={index === 0 ? `${startOffset}em` : `${lineSpacing * 2}em`}>
              {renderTextWithItalics(line.trim(), index)}
            </tspan>
          ))}
        </>
      );
    }
    
    // Single line with potential italics
    return <tspan>{renderTextWithItalics(text)}</tspan>;
  };

  const renderProfessionalStoryWheel = () => {
    const centerX = 500;
    const centerY = 330;
    const radius = 250;

    const allJourneyBeatsCompleted = (() => {
  const journeyBeats = ['aHero', 'meetAndGreet', 'catalyst', 'umAndAh', 'leap', 'upAndAtEm', 'complication', 'touchAndGo', 'turn', 'downAndDirty', 'risk', 'crashAndBurn', 'fall', 'riseAndShine', 'reveal', 'triedAndTested'];
  console.log('Checking beats completion:', journeyBeats.map(beatId => ({
    beat: beatId,
    completed: story.storyBeats[beatId]?.completed,
    exists: !!story.storyBeats[beatId]
  })));
  return journeyBeats.every(beatId => story.storyBeats[beatId]?.completed === true);
})();

    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'flex-start', 
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '10px 20px',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}>
       <div style={{ 
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '1000px'
        }}>
          {/* Story Type Button - Left Side */}
          <div style={{
            display: 'flex',
            alignItems: 'center'
          }}>
            <button
              onClick={openStoryTypeModal}
              style={{
                background: story.storyType 
                  ? 'linear-gradient(135deg, #5dade2 0%, #3498db 100%)' 
                  : 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                border: '2px solid rgba(255,255,255,0.4)',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '25px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.05)';
                e.target.style.boxShadow = story.storyType 
                  ? '0 8px 25px rgba(93, 173, 226, 0.4)' 
                  : '0 8px 25px rgba(52, 152, 219, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = '0 4px 15px rgba(0,0,0,0.2)';
              }}
              title={story.storyType ? "Click to change story type" : "Choose your story type"}
            >
              {story.storyType ? (
                <>
                  {getStoryType(story.storyType).icon} {getStoryType(story.storyType).name}
                </>
              ) : (
                <>💡 Choose Story Type</>
              )}
            </button>
          </div>
          
          {/* Title Input - Center */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            justifyContent: 'center',
            margin: '0 20px'
          }}>
            <input
              type="text"
              style={{ 
                padding: '12px 16px', 
                border: '2px solid rgba(255,255,255,0.4)', 
                borderRadius: '25px', 
                fontSize: '16px',
                fontWeight: '600',
                outline: 'none',
                backgroundColor: 'rgba(255,255,255,0.9)',
                color: '#2c3e50',
                minWidth: '300px',
                maxWidth: '400px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              value={story.title}
              onChange={(e) => updateStory(null, 'title', e.target.value)}
              onFocus={() => {
                if (!story.storyType && !story.title) {
                  setShowStoryTypeModal(true);
                }
              }}
              placeholder="Enter your story title"
            />
          </div>
          
          {/* Beats/Sequences Toggle - Right Side */}
          <div style={{
            display: 'flex',
            alignItems: 'center'
          }}>
            <div style={{
              display: 'flex',
              backgroundColor: 'rgba(255,255,255,0.9)',
              borderRadius: '20px',
              padding: '4px',
              gap: '2px',
              border: '2px solid rgba(255,255,255,0.4)',
              boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
            }}>
              <button
                onClick={() => setWheelView('sequences')}
                style={{
                  background: wheelView === 'sequences' 
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                    : 'transparent',
                  color: wheelView === 'sequences' ? 'white' : '#2c3e50',
                  border: 'none',
                  padding: '8px 14px',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600',
                  transition: 'all 0.3s ease'
                }}
              >
                Sequences
              </button>
              <button
                onClick={() => setWheelView('beats')}
                style={{
                  background: wheelView === 'beats' 
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                    : 'transparent',
                  color: wheelView === 'beats' ? 'white' : '#2c3e50',
                  border: 'none',
                  padding: '8px 14px',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600',
                  transition: 'all 0.3s ease'
                }}
              >
                Beats
              </button>
            </div>
          </div>
        </div>
          
        <div style={{ 
          position: 'relative',
          width: '1000px',
          height: '750px',
          backgroundColor: 'white',
          borderRadius: '20px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
          overflow: 'hidden'
        }}>
          <svg width="1000" height="750" viewBox="0 0 1000 750" style={{ position: 'absolute', top: 0, left: 0 }}>
            <defs>
              <radialGradient id="centerGradient" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#ffffff" stopOpacity="1"/>
                <stop offset="100%" stopColor="#f8f9fa" stopOpacity="1"/>
              </radialGradient>
              
              <radialGradient id="setUpGradient" cx="100%" cy="0%" r="180%">
                <stop offset="0%" stopColor="#1976D2" stopOpacity="0.7"/>
                <stop offset="70%" stopColor="#64B5F6" stopOpacity="0.3"/>
                <stop offset="100%" stopColor="#E3F2FD" stopOpacity="0.1"/>
              </radialGradient>
              
              <radialGradient id="buildUpGradient" cx="100%" cy="100%" r="180%">
                <stop offset="0%" stopColor="#2E7D32" stopOpacity="0.7"/>
                <stop offset="70%" stopColor="#81C784" stopOpacity="0.3"/>
                <stop offset="100%" stopColor="#E8F5E8" stopOpacity="0.1"/>
              </radialGradient>
              
              <radialGradient id="breakUpGradient" cx="0%" cy="100%" r="180%">
                <stop offset="0%" stopColor="#C62828" stopOpacity="0.7"/>
                <stop offset="70%" stopColor="#E57373" stopOpacity="0.3"/>
                <stop offset="100%" stopColor="#FFEBEE" stopOpacity="0.1"/>
              </radialGradient>
              
              <radialGradient id="wrapUpGradient" cx="0%" cy="0%" r="180%">
                <stop offset="0%" stopColor="#7B1FA2" stopOpacity="0.7"/>
                <stop offset="70%" stopColor="#BA68C8" stopOpacity="0.3"/>
                <stop offset="100%" stopColor="#F3E5F5" stopOpacity="0.1"/>
              </radialGradient>
              
              <filter id="dropShadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="2" dy="2" stdDeviation="3" floodColor="#000000" floodOpacity="0.3"/>
              </filter>
            </defs>

            <path d={`M ${centerX} ${centerY} L ${centerX} 0 L 1000 0 L 1000 ${centerY} Z`} fill="url(#setUpGradient)" />
            <path d={`M ${centerX} ${centerY} L 1000 ${centerY} L 1000 750 L ${centerX} 750 Z`} fill="url(#buildUpGradient)" />
            <path d={`M ${centerX} ${centerY} L ${centerX} 750 L 0 750 L 0 ${centerY} Z`} fill="url(#breakUpGradient)" />
            <path d={`M ${centerX} ${centerY} L 0 ${centerY} L 0 0 L ${centerX} 0 Z`} fill="url(#wrapUpGradient)" />

            <path
              d={`M ${centerX} ${centerY} L ${centerX} ${centerY - radius} A ${radius} ${radius} 0 0 1 ${centerX + radius * Math.sin(Math.PI/4)} ${centerY - radius * Math.cos(Math.PI/4)} Z`}
              fill="#2196F3"
              opacity="0.8"
            />
            
            <path
              d={`M ${centerX} ${centerY} L ${centerX + radius * Math.sin(Math.PI/4)} ${centerY - radius * Math.cos(Math.PI/4)} A ${radius} ${radius} 0 0 1 ${centerX + radius} ${centerY} Z`}
              fill="#1976D2"
              opacity="0.8"
            />
            
            <path
              d={`M ${centerX} ${centerY} L ${centerX + radius} ${centerY} A ${radius} ${radius} 0 0 1 ${centerX + radius * Math.sin(Math.PI/4)} ${centerY + radius * Math.cos(Math.PI/4)} Z`}
              fill="#4CAF50"
              opacity="0.8"
            />
            
            <path
              d={`M ${centerX} ${centerY} L ${centerX + radius * Math.sin(Math.PI/4)} ${centerY + radius * Math.cos(Math.PI/4)} A ${radius} ${radius} 0 0 1 ${centerX} ${centerY + radius} Z`}
              fill="#fdd835"
              opacity="0.8"
            />
            
            <path
              d={`M ${centerX} ${centerY} L ${centerX} ${centerY + radius} A ${radius} ${radius} 0 0 1 ${centerX - radius * Math.sin(Math.PI/4)} ${centerY + radius * Math.cos(Math.PI/4)} Z`}
              fill="#fea02e"
              opacity="0.8"
            />
            
            <path
              d={`M ${centerX} ${centerY} L ${centerX - radius * Math.sin(Math.PI/4)} ${centerY + radius * Math.cos(Math.PI/4)} A ${radius} ${radius} 0 0 1 ${centerX - radius} ${centerY} Z`}
              fill="#D32F2F"
              opacity="0.8"
            />
            
            <path
              d={`M ${centerX} ${centerY} L ${centerX - radius} ${centerY} A ${radius} ${radius} 0 0 1 ${centerX - radius * Math.sin(Math.PI/4)} ${centerY - radius * Math.cos(Math.PI/4)} Z`}
              fill="#E91E63"
              opacity="0.8"
            />
            
            <path
              d={`M ${centerX} ${centerY} L ${centerX - radius * Math.sin(Math.PI/4)} ${centerY - radius * Math.cos(Math.PI/4)} A ${radius} ${radius} 0 0 1 ${centerX} ${centerY - radius} Z`}
              fill="#a85cd9"
              opacity="0.8"
            />

            <line
              x1="0"
              y1={centerY}
              x2="1000"
              y2={centerY}
              stroke="#bdc3c7"
              strokeWidth="2"
              opacity="0.4"
            />
            
            <line
              x1={centerX}
              y1="0"
              x2={centerX}
              y2="750"
              stroke="#bdc3c7"
              strokeWidth="2"
              opacity="0.4"
            />
            
            <line
              x1={centerX - radius * Math.cos(Math.PI/4)}
              y1={centerY - radius * Math.sin(Math.PI/4)}
              x2={centerX + radius * Math.cos(Math.PI/4)}
              y2={centerY + radius * Math.sin(Math.PI/4)}
              stroke="#d5d8dc"
              strokeWidth="1.5"
              opacity="0.3"
            />
            
            <line
              x1={centerX - radius * Math.cos(Math.PI/4)}
              y1={centerY + radius * Math.sin(Math.PI/4)}
              x2={centerX + radius * Math.cos(Math.PI/4)}
              y2={centerY - radius * Math.sin(Math.PI/4)}
              stroke="#d5d8dc"
              strokeWidth="1.5"
              opacity="0.3"
            />

            {/* Tapered diameter separators - only in beats mode */}
            {wheelView === 'beats' && (() => {
              const separators = [];
              
              // 4 tapered lines that go across the entire circle, creating 8 slices
              for (let i = 0; i < 4; i++) {
                const angle = (i * 45) * (Math.PI / 180); // 45° apart = 8 slices
                
                // End points of the diameter
                const x1 = centerX + Math.cos(angle) * radius;
                const y1 = centerY + Math.sin(angle) * radius;
                const x2 = centerX - Math.cos(angle) * radius;
                const y2 = centerY - Math.sin(angle) * radius;
                
                // Perpendicular angle for width calculation
                const perpAngle = angle + Math.PI / 2;
                
                // Width at edges and center
                const edgeWidth = 25; // strokeWidth="50" equivalent
                const centerWidth = 1; // strokeWidth="2" equivalent
                
                // Calculate the 4 points of the bow tie shape
                const edge1Top = {
                  x: x1 + Math.cos(perpAngle) * edgeWidth,
                  y: y1 + Math.sin(perpAngle) * edgeWidth
                };
                const edge1Bottom = {
                  x: x1 - Math.cos(perpAngle) * edgeWidth,
                  y: y1 - Math.sin(perpAngle) * edgeWidth
                };
                const centerTop = {
                  x: centerX + Math.cos(perpAngle) * centerWidth,
                  y: centerY + Math.sin(perpAngle) * centerWidth
                };
                const centerBottom = {
                  x: centerX - Math.cos(perpAngle) * centerWidth,
                  y: centerY - Math.sin(perpAngle) * centerWidth
                };
                const edge2Top = {
                  x: x2 + Math.cos(perpAngle) * edgeWidth,
                  y: y2 + Math.sin(perpAngle) * edgeWidth
                };
                const edge2Bottom = {
                  x: x2 - Math.cos(perpAngle) * edgeWidth,
                  y: y2 - Math.sin(perpAngle) * edgeWidth
                };
                
                // Create bow tie path
                const pathData = `M ${edge1Top.x} ${edge1Top.y} 
                                 L ${centerTop.x} ${centerTop.y} 
                                 L ${edge2Top.x} ${edge2Top.y} 
                                 L ${edge2Bottom.x} ${edge2Bottom.y} 
                                 L ${centerBottom.x} ${centerBottom.y} 
                                 L ${edge1Bottom.x} ${edge1Bottom.y} 
                                 Z`;
                
                separators.push(
                  <path
                    key={i}
                    d={pathData}
                    fill="#BABABA"
                    stroke="#2c3e50" 
                    strokeWidth="1"
                    opacity="0.7"
                  />
                );
              }
              return separators;
            })()}

            <circle
              cx={centerX}
              cy={centerY}
              r={radius}
              fill="none"
              stroke="#2c3e50"
              strokeWidth="4"
              filter="url(#dropShadow)"
            />

            <g 
              style={getHoverStyle('center-story')}
              onMouseEnter={() => setHoveredElement('center-story')}
              onMouseLeave={() => setHoveredElement(null)}
              onClick={() => setShowStoryModal(true)}
            >
              <circle 
                cx={centerX} 
                cy={centerY} 
                r="60" 
                fill="transparent" 
              />
              <text
                x={centerX + 3}
                y={centerY + 12}
                textAnchor="middle"
                fontSize="36"
                fontWeight="bold"
                fill="#2c3e50"
                letterSpacing="3px"
                filter="url(#dropShadow)"
                style={{ pointerEvents: 'none' }}
              >
                STORY
              </text>
            </g>

            {wheelView === 'beats' && (
              <text 
                x="500" 
                y="65"
                textAnchor="middle" 
                fontSize={allJourneyBeatsCompleted ? "28" : "18"}
                fontWeight="bold" 
                fill={allJourneyBeatsCompleted ? "#FF4500" : "black"}
                stroke="none"
                strokeWidth="0"
                style={{ 
                  ...getHoverStyle(allJourneyBeatsCompleted ? 'theHero' : 'aHero')
                }}
                onMouseEnter={() => setHoveredElement(allJourneyBeatsCompleted ? 'theHero' : 'aHero')}
                onMouseLeave={() => setHoveredElement(null)}
                onClick={() => setSelectedBeat(allJourneyBeatsCompleted ? 'theHero' : 'aHero')}
              >
                {allJourneyBeatsCompleted ? "THE HERO" : "A Hero"} {story.storyBeats[allJourneyBeatsCompleted ? 'theHero' : 'aHero']?.completed ? "✓" : ""}

              </text>
            )}
            
            {wheelView === 'beats' && (
              <text 
                x="575" 
                y="180" 
                textAnchor="middle" 
                fontSize="18" 
                fill="black"
                style={getHoverStyle('meetAndGreet')}
                onMouseEnter={() => setHoveredElement('meetAndGreet')}
                onMouseLeave={() => setHoveredElement(null)}
                onClick={() => setSelectedBeat('meetAndGreet')}
              >
                <tspan x="565" dy="-0.3em">Meet &</tspan>
                <tspan x="565" dy="1.2em">Greet</tspan>
                {story.storyBeats['meetAndGreet']?.completed && <tspan x="595" dy="0em"> ✓</tspan>}
              </text>
           )}
            
            {wheelView === 'sequences' && (
              <text 
                x="525" 
                y="180" 
                textAnchor="middle" 
                fontSize="18"
                fill="black"
                style={getHoverStyle('grounding')}
                onMouseEnter={() => setHoveredElement('grounding')}
                onMouseLeave={() => setHoveredElement(null)}
                onClick={() => setSelectedSequence('grounding')}
              >
                {renderSequenceText(sequenceDetails.grounding.name, "560")} {story.storySequences.grounding?.completed ? "✓" : ""}
              </text>
            )}
            
            {wheelView === 'beats' && (
  <text 
    x="730" 
    y="140" 
    textAnchor="middle" 
    fontSize="18" 
    fontWeight="bold" 
    fill="black"
    style={getHoverStyle('catalyst')}
    onMouseEnter={() => setHoveredElement('catalyst')}
    onMouseLeave={() => setHoveredElement(null)}
    onClick={() => setSelectedBeat('catalyst')}
  >
    The Push {story.storyBeats['catalyst']?.completed ? "✓" : ""}
  </text>
)}
            
{wheelView === 'beats' && (
              <text 
                x="650" 
                y="280" 
                textAnchor="middle" 
                fontSize="18" 
                fill="black"
                style={getHoverStyle('umAndAh')}
                onMouseEnter={() => setHoveredElement('umAndAh')}
                onMouseLeave={() => setHoveredElement(null)}
                onClick={() => setSelectedBeat('umAndAh')}
              >
                Um & Ah {story.storyBeats['umAndAh']?.completed ? "✓" : ""}
              </text>
            )}
            
            {wheelView === 'sequences' && (
              <text 
                x="650" 
                y="280" 
                textAnchor="middle" 
                fontSize="18" 
                fill="black"
                style={getHoverStyle('disruption')}
                onMouseEnter={() => setHoveredElement('disruption')}
                onMouseLeave={() => setHoveredElement(null)}
                onClick={() => setSelectedSequence('disruption')}
              >
                {renderSequenceText(sequenceDetails.disruption.name, "650")} {story.storySequences.disruption?.completed ? "✓" : ""}
              </text>
            )}
            
            {wheelView === 'beats' && (
              <text 
                x="805" 
                y="335" 
                textAnchor="middle" 
                fontSize="18" 
                fontWeight="bold" 
                fill="black"
                style={getHoverStyle('leap')}
                onMouseEnter={() => setHoveredElement('leap')}
                onMouseLeave={() => setHoveredElement(null)}
                onClick={() => setSelectedBeat('leap')}
              >
                The Leap {story.storyBeats['leap']?.completed ? "✓" : ""}
              </text>
            )}
            
            {wheelView === 'beats' && (
              <text 
                x="650" 
                y="390" 
                textAnchor="middle" 
                fontSize="18" 
                fill="black"
                style={getHoverStyle('upAndAtEm')}
                onMouseEnter={() => setHoveredElement('upAndAtEm')}
                onMouseLeave={() => setHoveredElement(null)}
                onClick={() => setSelectedBeat('upAndAtEm')}
              >
                Up & At 'Em {story.storyBeats['upAndAtEm']?.completed ? "✓" : ""}
              </text>
            )}
            
            {wheelView === 'sequences' && (
              <text 
                x="650" 
                y="380" 
                textAnchor="middle" 
                fontSize="18" 
                fill="black"
                style={getHoverStyle('exploration')}
                onMouseEnter={() => setHoveredElement('exploration')}
                onMouseLeave={() => setHoveredElement(null)}
                onClick={() => setSelectedSequence('exploration')}
              >
                {renderSequenceText(sequenceDetails.exploration.name, "650")} {story.storySequences.exploration?.completed ? "✓" : ""}
              </text>
            )}
            {wheelView === 'beats' && (
              <text 
                x="760" 
                y="545" 
                textAnchor="middle" 
                fontSize="18" 
                fontWeight="bold" 
                fill="black"
                style={getHoverStyle('complication')}
                onMouseEnter={() => setHoveredElement('complication')}
                onMouseLeave={() => setHoveredElement(null)}
                onClick={() => setSelectedBeat('complication')}
              >
            The Complication {story.storyBeats['complication']?.completed ? "✓" : ""}

              </text>
            )}
            
            {wheelView === 'beats' && (
              <text 
                x="575" 
                y="490" 
                textAnchor="middle" 
                fontSize="18" 
                fill="black"
                style={getHoverStyle('touchAndGo')}
                onMouseEnter={() => setHoveredElement('touchAndGo')}
                onMouseLeave={() => setHoveredElement(null)}
                onClick={() => setSelectedBeat('touchAndGo')}
              >
                <tspan x="565" dy="-0.3em">Touch</tspan>
                <tspan x="565" dy="1.2em">& Go</tspan>
               {story.storyBeats['touchAndGo']?.completed && <tspan x="593" dy="0em"> ✓</tspan>}
              </text>
            )}
            
            {wheelView === 'sequences' && (
              <text 
                x="565" 
                y="490" 
                textAnchor="middle" 
                fontSize="18" 
                fill="black"
                style={getHoverStyle('opposition')}
                onMouseEnter={() => setHoveredElement('opposition')}
                onMouseLeave={() => setHoveredElement(null)}
                onClick={() => setSelectedSequence('opposition')}
              >
                {renderSequenceText(sequenceDetails.opposition.name, "565")} {story.storySequences.opposition?.completed ? "✓" : ""}
              </text>
            )}
            
            {wheelView === 'beats' && (
              <text 
                x="500" 
                y="615" 
                textAnchor="middle" 
                fontSize="18" 
                fontWeight="bold" 
                fill="black"
                style={getHoverStyle('turn')}
                onMouseEnter={() => setHoveredElement('turn')}
                onMouseLeave={() => setHoveredElement(null)}
                onClick={() => setSelectedBeat('turn')}
              >
               The Turn {story.storyBeats['turn']?.completed ? "✓" : ""}
              </text>
            )}
            
            {wheelView === 'beats' && (
              <text 
                x="435" 
                y="490" 
                textAnchor="middle" 
                fontSize="18" 
                fill="black"
                style={getHoverStyle('downAndDirty')}
                onMouseEnter={() => setHoveredElement('downAndDirty')}
                onMouseLeave={() => setHoveredElement(null)}
                onClick={() => setSelectedBeat('downAndDirty')}
              >
                <tspan x="435" dy="-0.3em">Down</tspan>
                <tspan x="432" dy="1.2em">& Dirty</tspan>
                {story.storyBeats['downAndDirty']?.completed && <tspan x="468" dy="0em"> ✓</tspan>}
              </text>
            )}
{wheelView === 'sequences' && (
              <text 
                x="435" 
                y="490" 
                textAnchor="middle" 
                fontSize="18" 
                fill="black"
                style={getHoverStyle('reckoning')}
                onMouseEnter={() => setHoveredElement('reckoning')}
                onMouseLeave={() => setHoveredElement(null)}
                onClick={() => setSelectedSequence('reckoning')}
              >
                {renderSequenceText(sequenceDetails.reckoning.name, "430")} {story.storySequences.reckoning?.completed ? "✓" : ""}
              </text>
            )}
            
            {wheelView === 'beats' && (
              <text 
                x="280" 
                y="535" 
                textAnchor="middle" 
                fontSize="18" 
                fontWeight="bold" 
                fill="black"
                style={getHoverStyle('risk')}
                onMouseEnter={() => setHoveredElement('risk')}
                onMouseLeave={() => setHoveredElement(null)}
                onClick={() => setSelectedBeat('risk')}
              >
                The Risk {story.storyBeats['risk']?.completed ? "✓" : ""}
              </text>
            )}
            
            {wheelView === 'beats' && (
              <text 
                x="350" 
                y="390" 
                textAnchor="middle" 
                fontSize="18" 
                fill="black"
                style={getHoverStyle('crashAndBurn')}
                onMouseEnter={() => setHoveredElement('crashAndBurn')}
                onMouseLeave={() => setHoveredElement(null)}
                onClick={() => setSelectedBeat('crashAndBurn')}
              >
                Crash & Burn {story.storyBeats['crashAndBurn']?.completed ? "✓" : ""}
              </text>
            )}
            
            {wheelView === 'sequences' && (
              <text 
                x="350" 
                y="380" 
                textAnchor="middle" 
                fontSize="18" 
                fill="black"
                style={getHoverStyle('collapse')}
                onMouseEnter={() => setHoveredElement('collapse')}
                onMouseLeave={() => setHoveredElement(null)}
                onClick={() => setSelectedSequence('collapse')}
              >
                {renderSequenceText(sequenceDetails.collapse.name, "350")} {story.storySequences.collapse?.completed ? "✓" : ""}
              </text>
            )}
            
            {wheelView === 'beats' && (
              <text 
                x="200" 
                y="337" 
                textAnchor="middle" 
                fontSize="18" 
                fontWeight="bold" 
                fill="black"
                style={getHoverStyle('fall')}
                onMouseEnter={() => setHoveredElement('fall')}
                onMouseLeave={() => setHoveredElement(null)}
                onClick={() => setSelectedBeat('fall')}
              >
                The Fall {story.storyBeats['fall']?.completed ? "✓" : ""}
              </text>
            )}
            
            {wheelView === 'beats' && (
              <text 
                x="350" 
                y="280" 
                textAnchor="middle" 
                fontSize="18" 
                fill="black"
                style={getHoverStyle('riseAndShine')}
                onMouseEnter={() => setHoveredElement('riseAndShine')}
                onMouseLeave={() => setHoveredElement(null)}
                onClick={() => setSelectedBeat('riseAndShine')}
              >
                Rise & Shine {story.storyBeats['riseAndShine']?.completed ? "✓" : ""}
              </text>
            )}
            
            {wheelView === 'sequences' && (
              <text 
                x="345" 
                y="280" 
                textAnchor="middle" 
                fontSize="18" 
                fill="black"
                style={getHoverStyle('reflection')}
                onMouseEnter={() => setHoveredElement('reflection')}
                onMouseLeave={() => setHoveredElement(null)}
                onClick={() => setSelectedSequence('reflection')}
              >
                {renderSequenceText(sequenceDetails.reflection.name, "350")} {story.storySequences.reflection?.completed ? "✓" : ""}
              </text>
            )}
            
            {wheelView === 'beats' && (
              <text 
                x="270" 
                y="130" 
                textAnchor="middle" 
                fontSize="18" 
                fontWeight="bold" 
                fill="black"
                style={getHoverStyle('reveal')}
                onMouseEnter={() => setHoveredElement('reveal')}
                onMouseLeave={() => setHoveredElement(null)}
                onClick={() => setSelectedBeat('reveal')}
              >
                The Reveal {story.storyBeats['reveal']?.completed ? "✓" : ""}
              </text>
            )}
            
            {wheelView === 'beats' && (
              <text 
                x="435" 
                y="180" 
                textAnchor="middle" 
                fontSize="18" 
                fill="black"
                style={getHoverStyle('triedAndTested')}
                onMouseEnter={() => setHoveredElement('triedAndTested')}
                onMouseLeave={() => setHoveredElement(null)}
                onClick={() => setSelectedBeat('triedAndTested')}
              >
                <tspan x="433" dy="-0.3em">Tried &</tspan>
                <tspan x="433" dy="1.2em">Tested</tspan>
                {story.storyBeats['triedAndTested']?.completed && <tspan x="467" dy="0em"> ✓</tspan>}
              </text>
            )}
            
            {wheelView === 'sequences' && (
              <text 
                x="435" 
                y="180" 
                textAnchor="middle" 
                fontSize="18" 
                fill="black"
                style={getHoverStyle('rebirth')}
                onMouseEnter={() => setHoveredElement('rebirth')}
                onMouseLeave={() => setHoveredElement(null)}
                onClick={() => setSelectedSequence('rebirth')}
              >
                {renderSequenceText(sequenceDetails.rebirth.name, "430")} {story.storySequences.rebirth?.completed ? "✓" : ""}
              </text>
            )}

            <g 
              style={getHoverStyle('setUp')}
              onMouseEnter={() => setHoveredElement('setUp')}
              onMouseLeave={() => setHoveredElement(null)}
              onClick={() => setSelectedArc('setUp')}
            >
              <text x="900" y="40" textAnchor="middle" fontSize="32" fontWeight="bold" fill="#1976D2">
                Set Up {story.miniArcs.setUp?.completed ? "✓" : ""}
              </text>
              <text x="900" y="65" textAnchor="middle" fontSize="16" fill="#2c3e50" fontStyle="italic">
                Thesis
              </text>
            </g>
            
            <g 
              style={getHoverStyle('setUp')}
              onMouseEnter={() => setHoveredElement('setUp')}
              onMouseLeave={() => setHoveredElement(null)}
              onClick={() => setSelectedArc('setUp')}
            >
              <text x="900" y="40" textAnchor="middle" fontSize="32" fontWeight="bold" fill="#1976D2">
                Set Up {story.miniArcs.setUp?.completed ? "✓" : ""}
              </text>
              <text x="900" y="65" textAnchor="middle" fontSize="16" fill="#2c3e50" fontStyle="italic">
                Thesis
              </text>
            </g>
            
            <g 
              style={getHoverStyle('buildUp')}
              onMouseEnter={() => setHoveredElement('buildUp')}
              onMouseLeave={() => setHoveredElement(null)}
              onClick={() => setSelectedArc('buildUp')}
            >
              <text x="900" y="625" textAnchor="middle" fontSize="32" fontWeight="bold" fill="#2E7D32">
                Build Up {story.miniArcs.buildUp?.completed ? "✓" : ""}
              </text>
              <text x="900" y="650" textAnchor="middle" fontSize="16" fill="#2c3e50" fontStyle="italic">
                Antithesis
              </text>
            </g>
            
            <g 
              style={getHoverStyle('breakUp')}
              onMouseEnter={() => setHoveredElement('breakUp')}
              onMouseLeave={() => setHoveredElement(null)}
              onClick={() => setSelectedArc('breakUp')}
            >
              <text x="100" y="625" textAnchor="middle" fontSize="32" fontWeight="bold" fill="#C62828">
                Break Up {story.miniArcs.breakUp?.completed ? "✓" : ""}
              </text>
              <text x="100" y="650" textAnchor="middle" fontSize="16" fill="#2c3e50" fontStyle="italic">
                Rethesis
              </text>
            </g>
            
            <g 
              style={getHoverStyle('wrapUp')}
              onMouseEnter={() => setHoveredElement('wrapUp')}
              onMouseLeave={() => setHoveredElement(null)}
              onClick={() => setSelectedArc('wrapUp')}
            >
              <text x="100" y="40" textAnchor="middle" fontSize="32" fontWeight="bold" fill="#7B1FA2">
                Wrap Up {story.miniArcs.wrapUp?.completed ? "✓" : ""}
              </text>
              <text x="100" y="65" textAnchor="middle" fontSize="16" fill="#2c3e50" fontStyle="italic">
                Synthesis
              </text>
            </g>

            <g 
              style={getHoverStyle('wrapUp-want-need')}
              onMouseEnter={() => setHoveredElement('wrapUp-want-need')}
              onMouseLeave={() => setHoveredElement(null)}
              onClick={() => setSelectedWantNeed('wrapUp')}
            >
              <text x="90" y="305" textAnchor="middle" fontSize="22" fontWeight="bold" fill="#7f8c8d">
                NEED = WANT
              </text>
            </g>
            <g 
              style={getHoverStyle('breakUp-want-need')}
              onMouseEnter={() => setHoveredElement('breakUp-want-need')}
              onMouseLeave={() => setHoveredElement(null)}
              onClick={() => setSelectedWantNeed('breakUp')}
            >
              <text x="90" y="370" textAnchor="middle" fontSize="22" fontWeight="bold" fill="#7f8c8d">
                NEED &gt; WANT
              </text>
            </g>
            
            <g 
              style={getHoverStyle('setUp-want-need')}
              onMouseEnter={() => setHoveredElement('setUp-want-need')}
              onMouseLeave={() => setHoveredElement(null)}
              onClick={() => setSelectedWantNeed('setUp')}
            >
              <text x="910" y="305" textAnchor="middle" fontSize="22" fontWeight="bold" fill="#7f8c8d">
                WANT ≠ NEED
              </text>
            </g>
            <g 
              style={getHoverStyle('buildUp-want-need')}
              onMouseEnter={() => setHoveredElement('buildUp-want-need')}
              onMouseLeave={() => setHoveredElement(null)}
              onClick={() => setSelectedWantNeed('buildUp')}
            >
              <text x="910" y="370" textAnchor="middle" fontSize="22" fontWeight="bold" fill="#7f8c8d">
                WANT &gt; NEED
              </text>
            </g>

<text 
    x="500" 
    y="30" 
    textAnchor="middle" 
    fontSize="24" 
    fontWeight="bold" 
    fill="#7f8c8d" 
    letterSpacing="2px"
    style={getHoverStyle('rightSideUp')}
    onMouseEnter={() => setHoveredElement('rightSideUp')}
    onMouseLeave={() => setHoveredElement(null)}
    onClick={() => setSelectedKnowledge('rightSideUp')}
  >
    RIGHT SIDE UP WORLD
  </text>
            <g transform="rotate(180 500 620)">
  <text 
    x="500" 
    y="605" 
    textAnchor="middle" 
    fontSize="24" 
    fontWeight="bold" 
    fill="#7f8c8d" 
    letterSpacing="2px"
    style={getHoverStyle('upsideDown')}
    onMouseEnter={() => setHoveredElement('upsideDown')}
    onMouseLeave={() => setHoveredElement(null)}
    onClick={() => setSelectedKnowledge('upsideDown')}
  >
    UPSIDE DOWN WORLD
  </text>
</g>
          </svg>
        </div>
      </div>
    );
  };

  
  const renderKnowledgeModal = () => {
    if (!selectedKnowledge) return null;
    
  console.log('selectedKnowledge:', selectedKnowledge);
  console.log('Available knowledge keys:', Object.keys(knowledgeContent));  
    
    const knowledge = knowledgeContent[selectedKnowledge];
if (!knowledge) return null;
    
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
       <div style={{
  backgroundColor: 'white',
  borderRadius: '16px',
  padding: '32px',
  width: '800px',
  minWidth: '400px',
  minHeight: '300px',
  maxWidth: '95vw',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: '0 25px 50px rgba(0,0,0,0.4)',
border: `4px solid ${selectedKnowledge === 'storyOverview' ? '#9b59b6' : selectedKnowledge && selectedKnowledge.startsWith('antagonist') ? '#e74c3c' : selectedKnowledge === 'upsideDown' ? '#e42e54' : selectedKnowledge === 'thematicStory' ? '#3498db' : selectedKnowledge === 'tangibleStory' ? '#e74c3c' : selectedKnowledge && selectedKnowledge.startsWith('protagonist') ? '#3498db' : ['premise', 'dilemma', 'theLie', 'theTruth', 'moralStakes'].includes(selectedKnowledge) ? '#16a09c' : selectedKnowledge === 'setUp' ? '#4A90E2' : selectedKnowledge === 'buildUp' ? '#7ED321' : selectedKnowledge === 'breakUp' ? '#e42e54' : selectedKnowledge === 'wrapUp' ? '#a85cd9' : selectedKnowledge === 'aHero' ? '#CD7F32' : selectedKnowledge === 'meetAndGreet' ? '#42A5F5' : selectedKnowledge === 'catalyst' ? '#3097ED' : selectedKnowledge === 'umAndAh' ? '#1E88E5' : selectedKnowledge === 'leap' ? '#5DAAA5' : selectedKnowledge === 'upAndAtEm' ? '#9CCC65' : selectedKnowledge === 'complication' ? '#CEDC50' : selectedKnowledge === 'touchAndGo' ? '#FFEB3B' : selectedKnowledge === 'turn' ? '#F28D48' : selectedKnowledge === 'downAndDirty' ? '#fea02e' : selectedKnowledge === 'risk' ? '#E74C3C' : selectedKnowledge === 'crashAndBurn' ? '#D32F2F' : selectedKnowledge === 'fall' ? '#E03855' : selectedKnowledge === 'riseAndShine' ? '#EC407A' : selectedKnowledge === 'reveal' ? '#CA4EAA' : selectedKnowledge === 'triedAndTested' ? '#a85cd9' : selectedKnowledge === 'theHero' ? '#DAA520' : selectedKnowledge === 'grounding' ? '#2196F3' : selectedKnowledge === 'disruption' ? '#1976D2' : selectedKnowledge === 'exploration' ? '#4CAF50' : selectedKnowledge === 'opposition' ? '#fdd835' : selectedKnowledge === 'reckoning' ? '#fea02e' : selectedKnowledge === 'collapse' ? '#D32F2F' : selectedKnowledge === 'reflection' ? '#E91E63' : selectedKnowledge === 'rebirth' ? '#a85cd9' : selectedKnowledge === 'secondaryCharacters' ? '#1E3A8A' : selectedKnowledge === 'themeDevelopment' ? '#16a09c' : '#9b59b6'}`,
  resize: 'both'
}}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
           <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: selectedKnowledge === 'storyOverview' ? '#9b59b6' : selectedKnowledge && selectedKnowledge.startsWith('antagonist') ? '#e74c3c' : selectedKnowledge === 'upsideDown' ? '#e42e54' : selectedKnowledge === 'thematicStory' ? '#3498db' : selectedKnowledge === 'tangibleStory' ? '#e74c3c' : selectedKnowledge && selectedKnowledge.startsWith('protagonist') ? '#3498db' : ['premise', 'dilemma', 'theLie', 'theTruth', 'moralStakes'].includes(selectedKnowledge) ? '#16a09c' : selectedKnowledge === 'setUp' ? '#4A90E2' : selectedKnowledge === 'buildUp' ? '#7ED321' : selectedKnowledge === 'breakUp' ? '#e42e54' : selectedKnowledge === 'wrapUp' ? '#a85cd9' : selectedKnowledge === 'aHero' ? '#CD7F32' : selectedKnowledge === 'meetAndGreet' ? '#42A5F5' : selectedKnowledge === 'catalyst' ? '#3097ED' : selectedKnowledge === 'umAndAh' ? '#1E88E5' : selectedKnowledge === 'leap' ? '#5DAAA5' : selectedKnowledge === 'upAndAtEm' ? '#9CCC65' : selectedKnowledge === 'complication' ? '#CEDC50' : selectedKnowledge === 'touchAndGo' ? '#FFEB3B' : selectedKnowledge === 'turn' ? '#F28D48' : selectedKnowledge === 'downAndDirty' ? '#fea02e' : selectedKnowledge === 'risk' ? '#E74C3C' : selectedKnowledge === 'crashAndBurn' ? '#D32F2F' : selectedKnowledge === 'fall' ? '#E03855' : selectedKnowledge === 'riseAndShine' ? '#EC407A' : selectedKnowledge === 'reveal' ? '#CA4EAA' : selectedKnowledge === 'triedAndTested' ? '#a85cd9' : selectedKnowledge === 'theHero' ? '#DAA520' : selectedKnowledge === 'grounding' ? '#2196F3' : selectedKnowledge === 'disruption' ? '#1976D2' : selectedKnowledge === 'exploration' ? '#4CAF50' : selectedKnowledge === 'opposition' ? '#fdd835' : selectedKnowledge === 'reckoning' ? '#fea02e' : selectedKnowledge === 'collapse' ? '#D32F2F' : selectedKnowledge === 'reflection' ? '#E91E63' : selectedKnowledge === 'rebirth' ? '#a85cd9' : selectedKnowledge === 'secondaryCharacters' ? '#1E3A8A' : selectedKnowledge === 'themeDevelopment' ? '#16a09c' : '#9b59b6'}}>
 {knowledge.title}
</h2>
            
<button
  onClick={() => setSelectedKnowledge(null)}
  style={{
  background: selectedKnowledge === 'storyOverview' ? '#9b59b6' : selectedKnowledge && selectedKnowledge.startsWith('antagonist') ? '#e74c3c' : selectedKnowledge === 'upsideDown' ? '#e42e54' : selectedKnowledge === 'thematicStory' ? '#3498db' : selectedKnowledge === 'tangibleStory' ? '#e74c3c' : selectedKnowledge && selectedKnowledge.startsWith('protagonist') ? '#3498db' : ['premise', 'dilemma', 'theLie', 'theTruth', 'moralStakes'].includes(selectedKnowledge) ? '#16a09c' : selectedKnowledge === 'setUp' ? '#4A90E2' : selectedKnowledge === 'buildUp' ? '#7ED321' : selectedKnowledge === 'breakUp' ? '#e42e54' : selectedKnowledge === 'wrapUp' ? '#a85cd9' : selectedKnowledge === 'aHero' ? '#CD7F32' : selectedKnowledge === 'meetAndGreet' ? '#42A5F5' : selectedKnowledge === 'catalyst' ? '#3097ED' : selectedKnowledge === 'umAndAh' ? '#1E88E5' : selectedKnowledge === 'leap' ? '#5DAAA5' : selectedKnowledge === 'upAndAtEm' ? '#9CCC65' : selectedKnowledge === 'complication' ? '#CEDC50' : selectedKnowledge === 'touchAndGo' ? '#FFEB3B' : selectedKnowledge === 'turn' ? '#F28D48' : selectedKnowledge === 'downAndDirty' ? '#fea02e' : selectedKnowledge === 'risk' ? '#E74C3C' : selectedKnowledge === 'crashAndBurn' ? '#D32F2F' : selectedKnowledge === 'fall' ? '#E03855' : selectedKnowledge === 'riseAndShine' ? '#EC407A' : selectedKnowledge === 'reveal' ? '#CA4EAA' : selectedKnowledge === 'triedAndTested' ? '#a85cd9' : selectedKnowledge === 'theHero' ? '#DAA520' : selectedKnowledge === 'grounding' ? '#2196F3' : selectedKnowledge === 'disruption' ? '#1976D2' : selectedKnowledge === 'exploration' ? '#4CAF50' : selectedKnowledge === 'opposition' ? '#fdd835' : selectedKnowledge === 'reckoning' ? '#fea02e' : selectedKnowledge === 'collapse' ? '#D32F2F' : selectedKnowledge === 'reflection' ? '#E91E63' : selectedKnowledge === 'rebirth' ? '#a85cd9' : selectedKnowledge === 'secondaryCharacters' ? '#1E3A8A' : selectedKnowledge === 'themeDevelopment' ? '#16a09c' : '#9b59b6',
    border: 'none',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    fontSize: '24px',
    cursor: 'pointer',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }}
>
              ×
            </button>
          </div>
          
          <p style={{ color: '#555', lineHeight: '1.7', fontSize: '16px' }}>
            {knowledge.content}
          </p>
          
          
        </div>
      </div>
    );
  };

  // Story Type Selection Modal Component
const renderStoryTypeModal = () => {
  if (!showStoryTypeModal) return null;
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '32px',
        width: '900px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 25px 50px rgba(0,0,0,0.4)',
        border: '4px solid #3498db'
      }}>
        {/* Modal Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
              <h2 style={{ 
                margin: 0, 
                fontSize: '28px', 
                fontWeight: '700', 
                color: '#2c3e50' 
              }}>
                What kind of story are you telling?
              </h2>
              
              <button
                onClick={() => setSelectedKnowledge('aboutStoryTypes')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '50%',
                  transition: 'all 0.2s ease',
                  color: '#3498db'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                }}
                title="Learn about story types"
              >
                📖
              </button>
            </div>
            <p style={{ 
              fontSize: '16px', 
              color: '#7f8c8d', 
              margin: '8px 0 0 0'
            }}>
              This will customize your experience with relevant examples, prompts, and structural guidance
            </p>
          </div>
          
          <button
            onClick={closeStoryTypeModal}
            style={{
              background: 'transparent',
              border: '1px solid #dee2e6',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#7f8c8d',
              padding: '8px',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f8f9fa';
              e.target.style.color = '#2c3e50';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#7f8c8d';
            }}
          >
            ✕
          </button>
</div>

        {/* Story Types Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '16px',
          marginBottom: '24px'
        }}>
          {Object.values(STORY_TYPES).map((storyType) => {
            const isSelected = tempStoryType === storyType.id;
            return (
              <div
                key={storyType.id}
                onClick={() => setTempStoryType(tempStoryType === storyType.id ? null : storyType.id)}
                style={{
                  border: `3px solid ${isSelected ? '#3498db' : '#dee2e6'}`,
                  borderRadius: '12px',
                  padding: '20px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  backgroundColor: isSelected ? '#f8fbff' : 'white',
                  transform: 'translateY(0)',
                  boxShadow: 'none'
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.borderColor = '#3498db';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(52, 152, 219, 0.2)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.borderColor = '#dee2e6';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div style={{ fontSize: '32px' }}>
                  {storyType.icon}
                </div>
                <span 
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedKnowledge(storyType.id);
                  }}
                  style={{ 
                    fontSize: '20px', 
                    cursor: 'pointer', 
                    padding: '4px',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                    transform: 'scale(1)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f8f9fa';
                    e.target.style.transform = 'scale(1.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  📖
                </span>
              </div>
              <div style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#2c3e50',
                marginBottom: '8px'
              }}>
                {storyType.name}
              </div>
              <div style={{
                fontSize: '14px',
                color: '#7f8c8d',
                marginBottom: '12px',
                lineHeight: '1.4'
              }}>
                {storyType.description}
              </div>
              <div style={{
                fontSize: '12px',
                color: '#3498db',
                fontStyle: 'italic'
              }}>
                {storyType.examples.join(', ')}
              </div>
              
              
            </div>
          );
        })}
        </div>

        {/* Modal Actions */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: '1px solid #dee2e6',
          paddingTop: '24px'
        }}>
          <button
            onClick={() => {
              setTempStoryType(story.storyType); // Reset temp selection
              closeStoryTypeModal();
            }}
            style={{
              background: 'transparent',
              border: '2px solid #dee2e6',
              color: '#7f8c8d',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '14px'
            }}
          >
            Skip for Now
          </button>
          
          <button
            onClick={() => {
              if (tempStoryType) {
                selectStoryType(tempStoryType); // Save the selection
              }
              closeStoryTypeModal();
            }}
            disabled={!tempStoryType}
            style={{
              background: tempStoryType ? '#3498db' : '#dee2e6',
              border: 'none',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: tempStoryType ? 'pointer' : 'not-allowed',
              fontWeight: '600',
              fontSize: '14px',
              opacity: tempStoryType ? 1 : 0.5
            }}
          >
            {tempStoryType ? `Continue with ${getStoryType(tempStoryType).name}` : 'Select a Story Type'}
          </button>
        </div>
      </div>
    </div>
  );
};

  const renderBeats = () => {
    const arcBeats = { "Set Up": [], "Build Up": [], "Break Up": [], "Wrap Up": [] };
    Object.entries(beatDetails).forEach(([beatId, details]) => {
      arcBeats[details.arc].push({ id: beatId, ...details });
    });

    return (
  <div style={{ padding: '24px', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
      <h2 style={{ fontSize: '36px', fontWeight: 'bold', margin: 0, color: '#2c3e50' }}>The 17 StoryBeats</h2>
      <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
        {renderPriorityLegend()}
        <button
          onClick={openStoryTypeModal}
          style={{
            background: story.storyType 
              ? 'linear-gradient(135deg, #5dade2 0%, #3498db 100%)' 
              : 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
            border: 'none',
            color: 'white',
            padding: '12px 20px',
            borderRadius: '25px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.05)';
            e.target.style.boxShadow = story.storyType 
              ? '0 8px 25px rgba(93, 173, 226, 0.4)' 
              : '0 8px 25px rgba(52, 152, 219, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
          }}
          title={story.storyType ? "Click to change story type" : "Choose your story type"}
        >
          {story.storyType ? (
            <>
              {getStoryType(story.storyType).icon} {getStoryType(story.storyType).name}
            </>
          ) : (
            <>💡 Choose Story Type</>
          )}
        </button>
      </div>
    </div>
    
    <p style={{ 
      fontSize: '18px', 
      color: '#666', 
      margin: '0 0 32px 0', 
      lineHeight: '1.5' 
    }}>
      Beats are the micro-moments that create consequential actions and emotional shifts in your story, providing precise narrative control and ensuring each scene or sequence serves your story's deeper purpose.
    </p>
    
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {Object.values(beatDetails).map(beat => {
            const beatId = Object.keys(beatDetails).find(key => beatDetails[key] === beat);
            const getBeatColorScheme = (beatId, arcName) => {
  if (individualBeatColors[beatId]) {
    return individualBeatColors[beatId];
  }
  // Convert arc names to keys and get colors
  const arcKey = arcName.toLowerCase().replace(' ', '');
  switch(arcName) {
    case 'Set Up': return { color: '#4A90E2', lightColor: '#E3F2FD' };
    case 'Build Up': return { color: '#7ED321', lightColor: '#E8F5E8' };
    case 'Break Up': return { color: '#e42e54', lightColor: '#fce8ed' };
    case 'Wrap Up': return { color: '#a85cd9', lightColor: '#f0e7f7' };
    default: return { color: '#6c757d', lightColor: '#f8f9fa' };
  }
};

            const getArcColor = (arcName) => {
              switch(arcName) {
                case 'Set Up': return '#4A90E2';
                case 'Build Up': return '#7ED321';
                case 'Break Up': return '#e42e54';
                case 'Wrap Up': return '#a85cd9';
                default: return '#6c757d';
              }
            };
            const arcColor = getArcColor(beat.arc);
                  const beatData = story.storyBeats[beatId] || { notes: '', completed: false };
                  
                  return (
                    <div key={beatId} style={{
                      backgroundColor: 'white', 
                      padding: '24px', 
                      borderRadius: '12px',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                      border: `3px solid ${getBeatColorScheme(beatId, beat.arc).color}`,
                      transition: 'all 0.3s ease'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <h4 style={{ 
                              fontWeight: '600', 
                              margin: 0,
                              fontSize: '28px',
                              color: getBeatColorScheme(beatId, beat.arc).color
                            }}>
                              {getPriorityMedal(beatId, 'beat')}{beat.name}
                            </h4>
                            <span 
                              onClick={() => setSelectedKnowledge(beatId)}
                              style={{ 
                                fontSize: '16px', 
                                cursor: 'pointer', 
                                padding: '2px',
                                borderRadius: '4px',
                                transition: 'all 0.2s ease',
                                transform: 'scale(1)'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#f8f9fa';
                                e.target.style.transform = 'scale(1.15)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.transform = 'scale(1)';
                              }}
                            >
                              📖
                            </span>
                          </div>
                          <p style={{ 
  fontSize: '16px', 
  color: '#666', 
  marginBottom: '0',
  lineHeight: '1.5'
}}>
  {getBeatDescription(selectedBeat)}
</p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', marginLeft: '20px' }}>
                          <label style={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            cursor: 'pointer',
                            fontSize: '16px',
                            fontWeight: '500'
                          }}>
                            <input
                              type="checkbox"
                              checked={beatData.completed}
                              onChange={(e) => updateStoryBeat(beatId, 'completed', e.target.checked)}
                              style={{ 
                                width: '20px', 
                                height: '20px',
                                marginRight: '8px',
                                cursor: 'pointer'
                              }}
                            />
                            Completed
                          </label>
                        </div>
                      </div>
                      
                      <div>                                              
                        {/* Add shaded guidance area */}
                        <div style={{ 
                          padding: '16px', 
                          backgroundColor: getBeatColorScheme(beatId, beat.arc).lightColor,
                          borderRadius: '8px',
                          marginBottom: '12px',
                          borderLeft: `4px solid ${getBeatColorScheme(beatId, beat.arc).color}`
                        }}>
                          <p style={{ margin: 0, color: '#555', lineHeight: '1.7', fontSize: '16px', }}>
                            {getBeatPrompt(beatId) || `Write your notes for this beat...`}
                          </p>
                        </div>
                        
                        <textarea
                          style={{
                            width: '100%', 
                            padding: '16px', 
                            border: `2px solid ${getBeatColorScheme(beatId, beat.arc).color}`,
                            borderRadius: '8px', 
                            height: '120px',
                            fontSize: '16px',
                            resize: 'vertical',
                            outline: 'none',
                            boxSizing: 'border-box'
                          }}
                          value={beatData.notes}
                          onChange={(e) => updateStoryBeat(beatId, 'notes', e.target.value)}
                          placeholder={`Write your notes for this beat...`}
                        />
                      </div>
                    </div>
                  );
                })}
        </div>
      </div>
    );
  };
const renderSequenceEditor = () => {
    if (!selectedSequence) return null;
    
    const sequenceInfo = sequenceDetails[selectedSequence];
    const sequenceData = story.storySequences[selectedSequence] || { notes: '', completed: false };
    const colorScheme = {
      color: sequenceDetails[selectedSequence].color,
      lightColor: sequenceDetails[selectedSequence].lightColor
    };
    
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '32px',
          width: '700px',
          minWidth: '400px',
          minHeight: '300px',
          maxHeight: '85vh',
          overflow: 'auto',
          boxShadow: '0 25px 50px rgba(0,0,0,0.4)',
          border: `4px solid ${colorScheme.color}`,
          resize: 'both'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '28px', fontWeight: '600', color: colorScheme.color }}>
                {getPriorityMedal(selectedSequence, 'sequence')}
                  {renderSequenceName(sequenceInfo.name)}
                </h3>
                <span 
                  onClick={() => setSelectedKnowledge(selectedSequence)}
                  style={{ 
                    fontSize: '20px', 
                    cursor: 'pointer', 
                    padding: '4px',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                    transform: 'scale(1)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f8f9fa';
                    e.target.style.transform = 'scale(1.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  📖
                </span>
              </div>
              <p style={{ margin: '8px 0', color: colorScheme.color, fontSize: '16px', fontWeight: '500' }}>
                {sequenceInfo.arc === 'setUp' ? 'Set Up' : sequenceInfo.arc === 'buildUp' ? 'Build Up' : sequenceInfo.arc === 'breakUp' ? 'Break Up' : sequenceInfo.arc === 'wrapUp' ? 'Wrap Up' : sequenceInfo.arc} Arc
              </p>
            </div>
            
            <button
              onClick={() => setSelectedSequence(null)}
              style={{
                background: colorScheme.color,
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                fontSize: '24px',
                cursor: 'pointer',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
          </div>
          
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px', 
              marginBottom: '20px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '500'
            }}>
              <input
                type="checkbox"
                checked={sequenceData.completed}
                onChange={(e) => updateSequence(selectedSequence, 'completed', e.target.checked)}
                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
              />
              Mark as completed
            </label>
            
            <label style={{ display: 'block', fontSize: '18px', fontWeight: '500', marginBottom: '12px', color: '#2c3e50' }}>
              Notes:
            </label>
            <textarea
              style={{
                width: '100%',
                padding: '16px',
                border: `2px solid ${colorScheme.color}`,
                borderRadius: '12px',
                height: '250px',
                fontSize: '16px',
                resize: 'vertical',
                outline: 'none',
                lineHeight: '1.5',
                boxSizing: 'border-box'
              }}
              value={sequenceData.notes}
              onChange={(e) => updateSequence(selectedSequence, 'notes', e.target.value)}
              placeholder={getSequencePrompt(selectedSequence)}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderTheme = () => (
    <div style={{ padding: '24px', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
        <h2 style={{ fontSize: '36px', fontWeight: 'bold', margin: 0, color: '#2c3e50' }}>Theme Development</h2>
        <span 
          onClick={() => setSelectedKnowledge('themeDevelopment')}
          style={{ 
            fontSize: '20px', 
            cursor: 'pointer', 
            padding: '4px',
            borderRadius: '4px',
            transition: 'all 0.2s ease',
            transform: 'scale(1)'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f8f9fa';
            e.target.style.transform = 'scale(1.15)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.transform = 'scale(1)';
          }}
        >
          📖
        </span>
      </div>
      <p className="guide-description" style={{ 
        fontSize: '18px', 
        color: '#666', 
        margin: '0 0 32px 0', 
        lineHeight: '1.5' 
      }}>
            Theme explores the deeper meaning of your story—what your protagonist learns about themself, and what your audience takes away from their journey.
          </p>
      
      <div style={{
        marginBottom: '32px', backgroundColor: 'white', padding: '32px',
        borderRadius: '16px', boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
        border: '3px solid #16a09c'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '28px', fontWeight: '600', margin: 0, color: '#495057' }}>The Theme Premise</h3>
          <span 
            onClick={() => setSelectedKnowledge('premise')}
            style={{ 
              fontSize: '20px', 
              cursor: 'pointer', 
              padding: '4px',
              borderRadius: '4px',
              transition: 'all 0.2s ease',
              transform: 'scale(1)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f8f9fa';
              e.target.style.transform = 'scale(1.15)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.transform = 'scale(1)';
            }}
          >
            📖
          </span>
        </div>
       <p style={{ marginBottom: '20px', fontSize: '16px', color: '#666', lineHeight: '1.6', fontStyle: 'italic' }}>
          A universal idea or belief the story is exploring.<br/>
          Example: <em>"If nothing matters, can love still mean something?"</em>
        </p>
        <textarea
          style={{ 
            width: '100%', 
            padding: '16px', 
            border: '2px solid #dee2e6', 
            borderRadius: '8px', 
            height: '160px',
            fontSize: '16px',
            resize: 'vertical',
            outline: 'none',
            boxSizing: 'border-box'
          }}
          value={story.themePremise || ''}
          onChange={(e) => updateStory(null, 'themePremise', e.target.value)}
placeholder="What's the larger truth or argument the story is wrestling with?"
        />
      </div>
      
      <div style={{
        marginBottom: '32px', backgroundColor: 'white', padding: '32px',
        borderRadius: '16px', boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
        border: '3px solid #16a09c'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '28px', fontWeight: '600', margin: 0, color: '#495057' }}>The Dilemma</h3>
          <span 
            onClick={() => setSelectedKnowledge('dilemma')}
            style={{ 
              fontSize: '20px', 
              cursor: 'pointer', 
              padding: '4px',
              borderRadius: '4px',
              transition: 'all 0.2s ease',
              transform: 'scale(1)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f8f9fa';
              e.target.style.transform = 'scale(1.15)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.transform = 'scale(1)';
            }}
          >
            📖
          </span>
        </div>
        <p style={{ marginBottom: '20px', fontSize: '16px', color: '#666', lineHeight: '1.6', fontStyle: 'italic' }}>
          A personal or internal crossroads the protagonist can't avoid.<br/>
          Example: <em>"Protect the family business or chase personal happiness?"</em>
        </p>
        <textarea
          style={{ 
            width: '100%', 
            padding: '16px', 
            border: '2px solid #dee2e6', 
            borderRadius: '8px', 
            height: '160px',
            fontSize: '16px',
            resize: 'vertical',
            outline: 'none',
            boxSizing: 'border-box'
          }}
          value={story.dilemma || ''}
          onChange={(e) => updateStory(null, 'dilemma', e.target.value)}
          placeholder="What internal tug-of-war forces the hero to choose between comfort and change?"
        />
      </div>
      
      <div style={{
        marginBottom: '32px', backgroundColor: 'white', padding: '32px',
        borderRadius: '16px', boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
        border: '3px solid #16a09c'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '28px', fontWeight: '600', margin: 0, color: '#495057' }}>The Lie</h3>
          <span 
            onClick={() => setSelectedKnowledge('theLie')}
            style={{ 
              fontSize: '20px', 
              cursor: 'pointer', 
              padding: '4px',
              borderRadius: '4px',
              transition: 'all 0.2s ease',
              transform: 'scale(1)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f8f9fa';
              e.target.style.transform = 'scale(1.15)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.transform = 'scale(1)';
            }}
          >
            📖
          </span>
        </div>
       <p style={{ marginBottom: '20px', fontSize: '16px', color: '#666', lineHeight: '1.6', fontStyle: 'italic' }}>
          A belief the protagonist clings to that limits their growth.<br/>
          Example: <em>"I'm only worthy if I succeed."</em>
        </p>
        <textarea
          style={{ 
            width: '100%', 
            padding: '16px', 
            border: '2px solid #dee2e6', 
            borderRadius: '8px', 
            height: '160px',
            fontSize: '16px',
            resize: 'vertical',
            outline: 'none',
            boxSizing: 'border-box'
          }}
          value={story.theLie || ''}
          onChange={(e) => updateStory(null, 'theLie', e.target.value)}
          placeholder="What false idea does the hero believe about themselves, others, or the world?"
        />
      </div>
      
      <div style={{
        marginBottom: '32px', backgroundColor: 'white', padding: '32px',
        borderRadius: '16px', boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
        border: '3px solid #16a09c'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '28px', fontWeight: '600', margin: 0, color: '#495057' }}>The Truth</h3>
          <span 
            onClick={() => setSelectedKnowledge('theTruth')}
            style={{ 
              fontSize: '20px', 
              cursor: 'pointer', 
              padding: '4px',
              borderRadius: '4px',
              transition: 'all 0.2s ease',
              transform: 'scale(1)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f8f9fa';
              e.target.style.transform = 'scale(1.15)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.transform = 'scale(1)';
            }}
          >
            📖
          </span>
        </div>
        <p style={{ marginBottom: '20px', fontSize: '16px', color: '#666', lineHeight: '1.6', fontStyle: 'italic' }}>
          A hard-earned lesson that counters the Lie.<br/>
          Example: <em>"My worth isn't tied to what I achieve."</em>
        </p>
        <textarea
          style={{ 
            width: '100%', 
            padding: '16px', 
            border: '2px solid #dee2e6', 
            borderRadius: '8px', 
            height: '160px',
            fontSize: '16px',
            resize: 'vertical',
            outline: 'none',
            boxSizing: 'border-box'
          }}
          value={story.theTruth || ''}
          onChange={(e) => updateStory(null, 'theTruth', e.target.value)}
          placeholder="What truth must the hero accept to grow, connect, or heal?"
        />
      </div>
      
      <div style={{
        marginBottom: '32px', backgroundColor: 'white', padding: '32px',
        borderRadius: '16px', boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
        border: '3px solid #16a09c'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '28px', fontWeight: '600', margin: 0, color: '#495057' }}>The Moral Stakes</h3>
          <span 
            onClick={() => setSelectedKnowledge('moralStakes')}
            style={{ 
              fontSize: '20px', 
              cursor: 'pointer', 
              padding: '4px',
              borderRadius: '4px',
              transition: 'all 0.2s ease',
              transform: 'scale(1)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f8f9fa';
              e.target.style.transform = 'scale(1.15)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.transform = 'scale(1)';
            }}
          >
            📖
          </span>
        </div>
        <p style={{ marginBottom: '20px', fontSize: '16px', color: '#666', lineHeight: '1.6', fontStyle: 'italic' }}>
          The emotional cost of the protagonist's choice—win or lose.<br/>
          Example: <em>Lose their child's trust, or rebuild the bond by finally showing up</em>
        </p>
        <textarea
          style={{ 
            width: '100%', 
            padding: '16px', 
            border: '2px solid #dee2e6', 
            borderRadius: '8px', 
            height: '160px',
            fontSize: '16px',
            resize: 'vertical',
            outline: 'none',
            boxSizing: 'border-box'
          }}
          value={story.moralStakes || ''}
          onChange={(e) => updateStory(null, 'moralStakes', e.target.value)}
          placeholder="What will be gained—or lost—if the hero does or doesn't embrace the truth?"
        />
      </div>
    </div>
  );

  // Helper function to render sequence names with italics for page display
  const renderSequenceName = (name) => {
    // Clean line breaks first
    const cleanedName = name.replace(/\|/g, ' ');
    
    if (!cleanedName.includes('*')) {
      return cleanedName;
    }
    
    const parts = cleanedName.split('*');
    return parts.map((part, index) => {
      if (index % 2 === 1) {
        return <em key={index}>{part}</em>;
      }
      return part;
    });
  };

// Helper function to get plain text sequence names for placeholders
  const getSequencePlainName = (name) => {
    return name.replace(/\|/g, ' ').replace(/\*/g, '');
  };

  const renderSequences = () => {
    const sequenceDescriptions = {
      grounding: 'Grounding',
      disruption: 'Disruption', 
      exploration: 'Exploration',
      opposition: 'Opposition',
      reckoning: 'Reckoning',
      collapse: 'Collapse',
      reflection: 'Reflection',
      rebirth: 'Rebirth'
    };

    // Colors now consolidated in sequenceDetails object

    return (
  <div style={{ padding: '24px', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
      <h2 style={{ fontSize: '36px', fontWeight: 'bold', margin: 0, color: '#2c3e50' }}>The 8 StorySequences</h2>
      <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
        {renderPriorityLegend()}
        <button
          onClick={openStoryTypeModal}
          style={{
            background: story.storyType 
              ? 'linear-gradient(135deg, #5dade2 0%, #3498db 100%)' 
              : 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
            border: 'none',
            color: 'white',
            padding: '12px 20px',
            borderRadius: '25px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.05)';
            e.target.style.boxShadow = story.storyType 
              ? '0 8px 25px rgba(93, 173, 226, 0.4)' 
              : '0 8px 25px rgba(52, 152, 219, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
          }}
          title={story.storyType ? "Click to change story type" : "Choose your story type"}
        >
          {story.storyType ? (
            <>
              {getStoryType(story.storyType).icon} {getStoryType(story.storyType).name}
            </>
          ) : (
            <>💡 Choose Story Type</>
          )}
        </button>
      </div>
    </div>
    <p style={{ fontSize: '18px', color: '#666', marginBottom: '32px', lineHeight: '1.6' }}>
      Sequences each provide a distinct narrative that advances the larger story while providing emotional journey phases that help track the protagonist's transformation through manageable chunks. They work in a meso-level structure that bridge the gap between arcs and beats.
    </p>
        
        {Object.entries(sequenceDetails).map(([sequenceKey, sequenceInfo]) => {
          const sequenceData = story.storySequences[sequenceKey] || { notes: '', completed: false };
          const colors = {
            color: sequenceDetails[sequenceKey].color,
            lightColor: sequenceDetails[sequenceKey].lightColor
          };
          
          return (
            <div key={sequenceKey} style={{
              marginBottom: '32px', 
              backgroundColor: 'white', 
              padding: '32px',
              borderRadius: '16px', 
              boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
              border: `3px solid ${colors.color}`
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                 <h3 style={{ fontSize: '28px', fontWeight: '600', color: colors.color, margin: 0 }}>
  {getPriorityMedal(sequenceKey, 'sequence')}{renderSequenceName(sequenceInfo.name)}
                  </h3>
                  <span 
                  onClick={() => setSelectedKnowledge(sequenceKey)}
                  style={{ 
                    fontSize: '20px', 
                    cursor: 'pointer', 
                    padding: '4px',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                    transform: 'scale(1)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f8f9fa';
                    e.target.style.transform = 'scale(1.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  📖
                </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <span style={{ fontSize: '16px', color: '#7f8c8d', fontStyle: 'italic' }}>
                    {sequenceDescriptions[sequenceKey]}
                  </span>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '500'
                  }}>
                    <input
                      type="checkbox"
                      checked={sequenceData.completed}
                      onChange={(e) => updateSequence(sequenceKey, 'completed', e.target.checked)}
                      style={{ 
                        width: '20px', 
                        height: '20px',
                        marginRight: '8px',
                        cursor: 'pointer'
                      }}
                    />
                    Completed
                  </label>
                </div>
              </div>
              
              <div style={{ 
                padding: '20px', 
                backgroundColor: colors.lightColor, 
                borderRadius: '12px',
                marginBottom: '20px',
                borderLeft: `6px solid ${colors.color}`
              }}>
                <p style={{ margin: 0, color: '#555', lineHeight: '1.7', fontSize: '16px' }}>
                  {getSequencePrompt(sequenceKey) || sequenceDetails[sequenceKey]?.description || 'Write your notes for this sequence...'}
                </p>
              </div>
              
              <textarea
                style={{ 
                  width: '100%', 
                  padding: '16px', 
                  border: `2px solid ${colors.color}`, 
                  borderRadius: '8px', 
                  height: '160px',
                  fontSize: '16px',
                  resize: 'vertical',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                value={sequenceData.notes}
                onChange={(e) => updateSequence(sequenceKey, 'notes', e.target.value)}
               placeholder={`Write your notes for this sequence...`}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const renderStructure = () => {
    const arcDescriptions = {
      setUp: 'Introduction',
      buildUp: 'Escalation', 
      breakUp: 'Dissolution',
      wrapUp: 'Resolution'
    };

    return (
      <div style={{ padding: '24px', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
        <div style={{ marginBottom: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '36px', fontWeight: 'bold', margin: 0, color: '#2c3e50' }}>The 4 StoryArcs</h2>
            <button
              onClick={openStoryTypeModal}
              style={{
                background: story.storyType 
                  ? 'linear-gradient(135deg, #5dade2 0%, #3498db 100%)' 
                  : 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                border: 'none',
                color: 'white',
                padding: '12px 20px',
                borderRadius: '25px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.05)';
                e.target.style.boxShadow = story.storyType 
                  ? '0 8px 25px rgba(93, 173, 226, 0.4)' 
                  : '0 8px 25px rgba(52, 152, 219, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
              }}
              title={story.storyType ? "Click to change story type" : "Choose your story type"}
            >
              {story.storyType ? (
                <>
                  {getStoryType(story.storyType).icon} {getStoryType(story.storyType).name}
                </>
              ) : (
                <>💡 Choose Story Type</>
              )}
            </button>
          </div>
          <p className="guide-description" style={{ 
            fontSize: '18px', 
            color: '#666', 
            margin: '8px 0 0 0', 
            lineHeight: '1.5' 
          }}>
            Arcs provide the macro structure of your story, representing the major emotional and plot movements that drive your story from beginning to end. Each serves a specific purpose as mini-stories ensuring an overall narrative arc that is engaging and meaningful. 
          </p>
        </div>
        
        {Object.entries(arcDetails).map(([arcKey, arc]) => {
          const arcData = story.miniArcs[arcKey] || { notes: '', completed: false };
          
          return (
            <div key={arcKey} style={{
              marginBottom: '32px', 
              backgroundColor: 'white', 
              padding: '32px',
              borderRadius: '16px', 
              boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
              border: `3px solid ${arc.color}`
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
<h3 style={{ fontSize: '28px', fontWeight: '600', color: arc.color, margin: 0 }}>
                    {arc.name} ({arcDescriptions[arcKey]})
                  </h3>
                  <span 
                  onClick={() => setSelectedKnowledge(arcKey)}
                  style={{ 
                    fontSize: '20px', 
                    cursor: 'pointer', 
                    padding: '4px',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                    transform: 'scale(1)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f8f9fa';
                    e.target.style.transform = 'scale(1.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  📖
                </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <span style={{ fontSize: '18px', color: '#7f8c8d', fontStyle: 'italic' }}>
                    {arc.subtitle}
                  </span>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '500'
                  }}>
                    <input
                      type="checkbox"
                      checked={arcData.completed}
                      onChange={(e) => updateArc(arcKey, 'completed', e.target.checked)}
                      style={{ 
                        width: '20px', 
                        height: '20px',
                        marginRight: '8px',
                        cursor: 'pointer'
                      }}
                    />
                    Completed
                  </label>
                </div>
              </div>
              
              <div style={{ 
                padding: '20px', 
                backgroundColor: arc.lightColor, 
                borderRadius: '12px',
                marginBottom: '20px',
                borderLeft: `6px solid ${arc.color}`
              }}>
                <p style={{ margin: 0, color: '#555', lineHeight: '1.7', fontSize: '16px' }}>
                  {getArcPrompt(arcKey) || arc.description}
                </p>
              </div>
              
              <textarea
                style={{ 
                  width: '100%', 
                  padding: '16px', 
                  border: `2px solid ${arc.color}`, 
                  borderRadius: '8px', 
                  height: '160px',
                  fontSize: '16px',
                  resize: 'vertical',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                value={arcData.notes}
                onChange={(e) => updateArc(arcKey, 'notes', e.target.value)}
                placeholder={`Write your notes for this arc...`}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const renderGuideModal = () => {
    if (!showGuideModal) return null;
    
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        padding: '20px'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          width: '95vw',
          height: '90vh',
          maxWidth: '1200px',
          display: 'flex',
          overflow: 'hidden',
          boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
          border: '4px solid #1abc9c'
        }}>
          {/* Sidebar Navigation */}
          <div style={{
            width: '280px',
            background: 'linear-gradient(135deg, #1abc9c 0%, #fdd835 100%)',
            padding: '32px 24px',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: 'white' }}>
                  📋 Guide
                </h2>
                
                <span 
                  onClick={() => setCurrentAudio('guideOverview')}
                  style={{ 
                    fontSize: '20px', 
                    cursor: 'pointer', 
                    padding: '4px',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                    transform: 'scale(1)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'rgba(255,255,255,0.2)';
                    e.target.style.transform = 'scale(1.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.transform = 'scale(1)';
                  }}
                  title="Listen to StoryUpNow guide discussion"
                >
                  🎧
                </span>
              </div>
              <button
                onClick={() => setShowGuideModal(false)}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            
            <nav style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                'Welcome to StoryUpNow',
                'What Makes StoryUpNow Different', 
                'Getting Started',
                'How to Use Each Section',
                'The Knowledge Window System',
                'Tips for Different Creative Approaches',
                'Advanced Features',
                'Troubleshooting & Support'
              ].map((section, index) => (
                <div
                  key={index}
                  style={{
                    color: 'white',
                    textDecoration: 'none',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    fontSize: activeGuideSection === index ? '15px' : '14px',
                    fontWeight: activeGuideSection === index ? '600' : '500',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    backgroundColor: activeGuideSection === index ? 'rgba(255,255,255,0.1)' : 'transparent',
                    transform: 'scale(1)',
                    boxShadow: 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (activeGuideSection !== index) {
                      e.target.style.backgroundColor = 'rgba(255,255,255,0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeGuideSection !== index) {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                  onClick={() => {
                    const element = document.getElementById(`section-${index}`);
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  {section}
                </div>
              ))}
            </nav>
          </div>

          {/* Main Content */}
          <div style={{
            flex: 1,
            padding: '32px 40px',
            overflow: 'auto',
            lineHeight: '1.7'
          }}>
            <div style={{ maxWidth: '800px' }}>
              
              <section id="section-0" style={{ marginBottom: '48px' }}>
                <h1 style={{ fontSize: '36px', fontWeight: '700', color: '#1abc9c', marginBottom: '24px' }}>
                  Welcome to StoryUpNow
                </h1>
                
                <p style={{ fontSize: '18px', color: '#555', marginBottom: '20px' }}>
                  We all think differently—some more different than others. We learn differently, read differently, and write differently. StoryUpNow isn't a "one size fits all" approach. There are as many ways to break a story as there are types of stories, and StoryUpNow gives you options on how to start your journey in a way that works for you.
                </p>
                
                <p style={{ fontSize: '18px', color: '#555', marginBottom: '20px' }}>
                  That being said, your story still needs to connect with an audience, and StoryUpNow makes sure that happens with time-tested storytelling techniques.
                </p>
                
                <p style={{ fontSize: '18px', color: '#555', marginBottom: '20px' }}>
                  Whether you're a <strong>Plotter</strong> who loves detailed outlines, a <strong>Pantser</strong> who prefers to discover the story while writing, or a <strong>Plantser</strong> who likes some structure but room for creative discovery—StoryUpNow adapts to your natural creative process. <strong>Plotters</strong> can use it as a comprehensive planning system, working through every section methodically. <strong>Pantsers</strong> can use it as a creative compass—just enough structure to keep the story emotionally grounded without constraining discoveries. <strong>Plantsers</strong> can use the broad framework for direction while letting their writing organically fill in the details. You can even shift between approaches within a single project, and StoryUpNow grows with your story however your creative process evolves.
                </p>
                
                <p style={{ fontSize: '18px', color: '#555', marginBottom: '20px' }}>
                  Some writers are visual thinkers who love seeing connections and relationships mapped out. Others are linear processors who prefer working through elements step-by-step. Still others are explorers who need the freedom to jump around as inspiration strikes. StoryUpNow honors all these approaches while ensuring your story has the emotional architecture that makes audiences care.
                </p>
                
                <p style={{ fontSize: '18px', color: '#555' }}>
                  The beauty of great storytelling is that it transcends individual differences. Whether you're crafting a screenplay, novel, short story, or any other narrative form, the emotional principles that make stories resonate are universal. StoryUpNow helps you find your unique path to those universal truths.
                </p>
              </section>

              <section id="section-1" style={{ marginBottom: '48px' }}>
                <h2 style={{ fontSize: '28px', fontWeight: '600', color: '#1abc9c', marginBottom: '24px' }}>
                  What Makes StoryUpNow Different
                </h2>
                
                <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginBottom: '16px' }}>
  Story Type-Adaptive Framework
</h3>
<p style={{ fontSize: '16px', color: '#555', marginBottom: '16px' }}>
  StoryUpNow provides <strong>intelligent, story-specific guidance</strong> that adapts to your narrative type:
</p>
<ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
  <li style={{ marginBottom: '8px' }}><strong>13 Story Types</strong>: Growing Pains, Fish Out of Water, Matters of the Heart, Second Chances, and more</li>
  <li style={{ marginBottom: '8px' }}><strong>Adaptive Beat Priorities</strong>: Essential beats highlighted for your story type</li>
  <li style={{ marginBottom: '8px' }}><strong>Customized Guidance</strong>: Examples and prompts tailored to your narrative approach</li>
  <li style={{ marginBottom: '8px' }}><strong>4 Planning Levels</strong>: 2 Stories → 4 Arcs → 8 Sequences → 17 Beats</li>
</ul>

<h3 style={{ fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginBottom: '16px' }}>
  The Modular Approach
</h3>
<p style={{ fontSize: '16px', color: '#555', marginBottom: '16px' }}>
  Work at whatever level feels most natural for your creative process:
</p>
<ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
  <li style={{ marginBottom: '8px' }}><strong>2 Stories</strong>: Tangible (what happens) + Thematic (what it means)</li>
  <li style={{ marginBottom: '8px' }}><strong>4 Arcs</strong>: Broad emotional movements through your story</li>
  <li style={{ marginBottom: '8px' }}><strong>8 Sequences</strong>: Focused journey phases that bridge arcs and beats</li>
  <li style={{ marginBottom: '8px' }}><strong>17 Beats</strong>: Specific story moments and revelations</li>
</ul>
                
                <p style={{ fontSize: '16px', color: '#555', marginBottom: '20px' }}>
                  <strong>Choose your level</strong>: Some writers love the big picture of arcs and need that foundation before they can think about details. Others get overwhelmed by too much abstraction and need concrete, specific guidance that beats provide. Many find sequences the perfect middle ground—more specific than arcs but less overwhelming than individual beats.
                </p>
                
                <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginBottom: '16px' }}>
                  Flexible Navigation
                </h3>
                <ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
                  <li style={{ marginBottom: '8px' }}><strong>Visual learners</strong> can use the StoryHub to see how everything connects</li>
                  <li style={{ marginBottom: '8px' }}><strong>Sequential processors</strong> can work through sections in order</li>
                  <li style={{ marginBottom: '8px' }}><strong>Non-linear thinkers</strong> can jump between any elements as inspiration strikes</li>
                  <li style={{ marginBottom: '8px' }}><strong>Detail-oriented writers</strong> can dive deep into individual beats</li>
                  <li style={{ marginBottom: '8px' }}><strong>Big-picture thinkers</strong> can focus on arcs and broad themes</li>
                </ul>
                
                <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginBottom: '16px' }}>
                  Proven but Adaptable Framework
                </h3>
                <p style={{ fontSize: '16px', color: '#555', marginBottom: '16px' }}>
                  While StoryUpNow provides multiple pathways, they all lead to the same destination: a story with solid emotional architecture. Every approach ensures your story has:
                </p>
                <ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
                  <li style={{ marginBottom: '8px' }}>A protagonist worth following through transformation</li>
                  <li style={{ marginBottom: '8px' }}>Meaningful conflict that tests character growth</li>
                  <li style={{ marginBottom: '8px' }}>Stakes that matter both externally and internally</li>
                  <li style={{ marginBottom: '8px' }}>A thematic core that gives the story resonance</li>
                  <li style={{ marginBottom: '8px' }}>Pacing that keeps audiences engaged</li>
                </ul>
              </section>

              <section id="section-2" style={{ marginBottom: '48px' }}>
                <h2 style={{ fontSize: '28px', fontWeight: '600', color: '#1abc9c', marginBottom: '24px' }}>
                  Getting Started
                </h2>
                
                <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginBottom: '16px' }}>
1. Start Where It Feels Right
</h3>
<p style={{ fontSize: '16px', color: '#555', marginBottom: '16px' }}>
  Begin wherever your story feels most alive and excites you the most:
</p>
<ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
  <li style={{ marginBottom: '8px' }}><strong>Have a character in mind?</strong> Start with Character Development.</li>
  <li style={{ marginBottom: '8px' }}><strong>Know your theme?</strong> Begin with the Theme page.</li>
  <li style={{ marginBottom: '8px' }}><strong>Have plot ideas?</strong> Jump into StoryBeats, StorySequences or StoryArcs.</li>
  <li style={{ marginBottom: '8px' }}><strong>Want the big picture first?</strong> Click the center STORY button on the StoryHub.</li>
  <li style={{ marginBottom: '8px' }}><strong>Feeling overwhelmed?</strong> Don't stress about it. Explore. Have fun. Click anything or everything, and you'll find the right spot that's right for you and your story.</li>
</ul>
                
                <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginBottom: '16px' }}>
  2. Choose Your Story Type 
</h3>
<p style={{ fontSize: '16px', color: '#555', marginBottom: '16px' }}>
  Start by selecting your story type for personalized guidance:
</p>
<ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
  <li style={{ marginBottom: '8px' }}><strong>Click the Story Type button</strong> in the header to open the selection modal.</li>
  <li style={{ marginBottom: '8px' }}><strong>Choose from 13 story types</strong> or select "Not Sure Yet" to explore.</li>
  <li style={{ marginBottom: '8px' }}><strong>Get customized beat priorities</strong> and examples for your narrative type.</li>
  <li style={{ marginBottom: '8px' }}><strong>Change anytime</strong> as your story evolves during development.</li>
</ul>

<h3 style={{ fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginBottom: '16px' }}>
  
                  3. Choose Your Working Level
                </h3>
                <ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
                  <li style={{ marginBottom: '8px' }}><strong>Need broad strokes?</strong> Use the 4 StoryArcs for big-picture planning.</li>
                  <li style={{ marginBottom: '8px' }}><strong>Want manageable chunks?</strong> Work with the 8 StorySequences.</li>
                  <li style={{ marginBottom: '8px' }}><strong>Prefer detailed guidance?</strong> Dive into all 17 StoryBeats.</li>
                  <li style={{ marginBottom: '8px' }}><strong>Like visual connections?</strong> Use the StoryHub as your home base.</li>
                  <li style={{ marginBottom: '8px' }}><strong>Enjoy listening to friendly discussions?</strong> Use the Audio Libraries to get you going.</li>
                </ul>
                
                <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginBottom: '16px' }}>
                  4. Build at Your Own Pace
                </h3>
                <ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
                  <li style={{ marginBottom: '8px' }}><strong>Perfectionist?</strong> Complete each section thoroughly before moving on.</li>
                  <li style={{ marginBottom: '8px' }}><strong>Iterative worker?</strong> Make multiple passes, refining as you go.</li>
                  <li style={{ marginBottom: '8px' }}><strong>Inspiration-driven?</strong> Fill in whatever section sparks your interest.</li>
                  <li style={{ marginBottom: '8px' }}><strong>Systematic planner?</strong> Work through each page in order.</li>
                </ul>
                
                <p style={{ fontSize: '16px', color: '#555', fontStyle: 'italic' }}>
                  Remember: there's no timer, no required completion order, and no judgment. Your story develops the way your mind works best.
                </p>
              </section>

              <section id="section-3" style={{ marginBottom: '48px' }}>
                <h2 style={{ fontSize: '28px', fontWeight: '600', color: '#1abc9c', marginBottom: '24px' }}>
                  How to Use Each Section
                </h2>
                
                <div style={{ marginBottom: '32px' }}>
  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginBottom: '16px' }}>
    Story Type Selection
  </h3>
  <p style={{ fontSize: '16px', color: '#555', marginBottom: '16px' }}>
    <strong>Choose your narrative approach for personalized guidance</strong>:
  </p>
  <ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
    <li style={{ marginBottom: '8px' }}><strong>13 Story Types available</strong>: What's your story most like?</li>
    <li style={{ marginBottom: '8px' }}><strong>Beat priority indicators</strong>: See which beats are essential vs. supporting for your story type</li>
    <li style={{ marginBottom: '8px' }}><strong>Customized examples</strong>: Get relevant movie/book references for your narrative approach</li>
    <li style={{ marginBottom: '8px' }}><strong>Flexible selection</strong>: Change story types anytime or choose "Not Sure Yet" to explore</li>
  </ul>
</div>

<div style={{ marginBottom: '32px' }}>
  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginBottom: '16px' }}>
    StoryHub
  </h3>
                  <p style={{ fontSize: '16px', color: '#555', marginBottom: '16px' }}>
                    Your <strong>visual command center</strong> where you can see the entire story framework at once:
                  </p>
                  <ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '8px' }}><strong>Toggle between Beats and Sequences</strong> to work at your preferred level of detail</li>
                    <li style={{ marginBottom: '8px' }}><strong>Click any element</strong> to open detailed editors</li>
                    <li style={{ marginBottom: '8px' }}><strong>Track progress</strong> with visual checkmarks</li>
                    <li style={{ marginBottom: '8px' }}><strong>See connections</strong> between different story elements</li>
                    <li style={{ marginBottom: '8px' }}><strong>Jump to any section</strong> that catches your attention</li>
                  </ul>
                </div>

                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginBottom: '16px' }}>
                    Characters Page
                  </h3>
                  <p style={{ fontSize: '16px', color: '#555', marginBottom: '16px' }}>
                    <strong>Build your cast systematically</strong>:
                  </p>
                  <ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '8px' }}><strong>Primary Characters</strong>: Develop your protagonist and antagonist with full emotional depth</li>
                    <li style={{ marginBottom: '8px' }}><strong>Supporting Characters</strong>: Use the role assignment system to ensure each character serves a purpose</li>
                    <li style={{ marginBottom: '8px', paddingLeft: '20px' }}><em>Tangible Roles</em>: How they affect the plot (Mentor, Rival, Sidekick, etc.)</li>
                    <li style={{ marginBottom: '8px', paddingLeft: '20px' }}><em>Thematic Roles</em>: How they relate to your story's deeper meaning (Believer, Doubter, etc.)</li>
                    <li style={{ marginBottom: '8px' }}><strong>Flexible classification</strong>: Organize characters as Major, Minor, or Draft as your story evolves</li>
                  </ul>
                </div>

                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginBottom: '16px' }}>
                    Theme Page
                  </h3>
                  <p style={{ fontSize: '16px', color: '#555', marginBottom: '16px' }}>
                    <strong>Develop your story's deeper meaning</strong> through five key elements:
                  </p>
                  <ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '8px' }}><strong>Theme Premise</strong>: The universal idea you're exploring</li>
                    <li style={{ marginBottom: '8px' }}><strong>The Dilemma</strong>: The internal conflict driving your story</li>
                    <li style={{ marginBottom: '8px' }}><strong>The Lie</strong>: What your protagonist wrongly believes</li>
                    <li style={{ marginBottom: '8px' }}><strong>The Truth</strong>: What they must learn to grow</li>
                    <li style={{ marginBottom: '8px' }}><strong>Moral Stakes</strong>: What's gained or lost through change</li>
                  </ul>
                  <p style={{ fontSize: '16px', color: '#555' }}>
                    Work through these in any order that makes sense for your story.
                  </p>
                </div>

                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginBottom: '16px' }}>
                    StoryArcs Page
                  </h3>
                  <p style={{ fontSize: '16px', color: '#555', marginBottom: '16px' }}>
                    <strong>Perfect for big-picture thinkers</strong>:
                  </p>
                  <ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '8px' }}><strong>Set Up</strong>: Establish the world and introduce the central conflict</li>
                    <li style={{ marginBottom: '8px' }}><strong>Build Up</strong>: Escalate tension and deepen emotional stakes</li>
                    <li style={{ marginBottom: '8px' }}><strong>Break Up</strong>: Everything falls apart, forcing transformation</li>
                    <li style={{ marginBottom: '8px' }}><strong>Wrap Up</strong>: Resolution and character growth</li>
                  </ul>
                  <p style={{ fontSize: '16px', color: '#555' }}>
                    Ideal for writers who need to understand the broad emotional journey before diving into specifics.
                  </p>
                </div>

                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginBottom: '16px' }}>
                    StorySequences Page
                  </h3>
                  <p style={{ fontSize: '16px', color: '#555', marginBottom: '16px' }}>
                    <strong>The sweet spot for many writers</strong>:
                  </p>
                  <ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '8px' }}>8 emotional journey phases that feel substantial but manageable</li>
                    <li style={{ marginBottom: '8px' }}>More specific than arcs, less overwhelming than individual beats</li>
                    <li style={{ marginBottom: '8px' }}>Great for tracking story momentum and emotional pacing</li>
                    <li style={{ marginBottom: '8px' }}>Perfect bridge between big-picture and detailed planning</li>
                  </ul>
                </div>

                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginBottom: '16px' }}>
                    StoryBeats Page
                  </h3>
                  <p style={{ fontSize: '16px', color: '#555', marginBottom: '16px' }}>
                    <strong>Maximum detail and step-by-step guidance</strong>:
                  </p>
                  <ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '8px' }}>17 specific story moments with detailed explanations</li>
                    <li style={{ marginBottom: '8px' }}>Concrete prompts and examples for each beat</li>
                    <li style={{ marginBottom: '8px' }}>Perfect for writers who prefer clear, specific direction</li>
                    <li style={{ marginBottom: '8px' }}>Ideal when you know what should happen but need help with execution</li>
                  </ul>
                </div>
              </section>

              <section id="section-4" style={{ marginBottom: '48px' }}>
                <h2 style={{ fontSize: '28px', fontWeight: '600', color: '#1abc9c', marginBottom: '24px' }}>
                  The Knowledge Window System
                </h2>
                
                <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginBottom: '16px' }}>
                  Using the 📖 Icons
                </h3>
                <p style={{ fontSize: '16px', color: '#555', marginBottom: '16px' }}>
                  Throughout StoryUpNow, you'll find 📖 icons next to various elements. These open <strong>knowledge windows</strong> that provide:
                </p>
                <ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
                  <li style={{ marginBottom: '8px' }}><strong>What It Means</strong>: Clear, jargon-free explanations</li>
                  <li style={{ marginBottom: '8px' }}><strong>Why It Matters</strong>: The purpose and emotional impact</li>
                  <li style={{ marginBottom: '8px' }}><strong>Examples</strong>: References from well-known movies and books</li>
                  <li style={{ marginBottom: '8px' }}><strong>Narrative Insight</strong>: Professional tips and deeper understanding</li>
                </ul>
                <p style={{ fontSize: '16px', color: '#555' }}>
                  Think of these as having a story consultant available whenever you need guidance. Use them liberally—they're designed to support your creative process, not interrupt it.
                </p>
              </section>

              <section id="section-5" style={{ marginBottom: '48px' }}>
                <h2 style={{ fontSize: '28px', fontWeight: '600', color: '#1abc9c', marginBottom: '24px' }}>
                  Tips for Different Creative Approaches
                </h2>
                
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50', marginBottom: '12px' }}>
                    Plot-First Writers
                  </h3>
                  <ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '6px' }}>Start with the <strong>Tangible Story</strong> overview to map your external events</li>
                    <li style={{ marginBottom: '6px' }}>Use <strong>StoryBeats</strong> for detailed scene-by-scene plotting</li>
                    <li style={{ marginBottom: '6px' }}>Develop your <strong>Antagonist</strong> early to create strong opposition</li>
                    <li style={{ marginBottom: '6px' }}>Pay special attention to turning points: <strong>The Turn</strong> and <strong>Tried & Tested</strong></li>
                  </ul>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50', marginBottom: '12px' }}>
                    Character-First Writers
                  </h3>
                  <ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '6px' }}>Begin with comprehensive <strong>Character development</strong> for all your main players</li>
                    <li style={{ marginBottom: '6px' }}>Focus on the <strong>Thematic Story</strong> to understand your characters' inner journeys</li>
                    <li style={{ marginBottom: '6px' }}>Use the <strong>Want vs Need</strong> framework to create compelling character arcs</li>
                    <li style={{ marginBottom: '6px' }}>Emphasize relationship beats: <strong>Meet & Greet</strong> and <strong>Rise & Shine</strong></li>
                  </ul>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50', marginBottom: '12px' }}>
                    Theme-First Writers
                  </h3>
                  <ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '6px' }}>Start with the complete <strong>Theme page</strong> to clarify what you're exploring</li>
                    <li style={{ marginBottom: '6px' }}>Carefully develop <strong>The Lie</strong> and <strong>The Truth</strong> as your story's emotional core</li>
                    <li style={{ marginBottom: '6px' }}>Use <strong>supporting character thematic roles</strong> to examine your theme from multiple angles</li>
                    <li style={{ marginBottom: '6px' }}>Keep <strong>moral stakes</strong> at the forefront throughout development</li>
                  </ul>
                </div>

                <div style={{ marginBottom: '24px' }}>
  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50', marginBottom: '12px' }}>
    "I'm Not Sure What Kind of Writer I Am"
  </h3>
  <ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
    <li style={{ marginBottom: '6px' }}>Start with <strong>Story Type selection</strong> to get personalized guidance that matches your story</li>
    <li style={{ marginBottom: '6px' }}>Choose <strong>"Not Sure Yet"</strong> story type for balanced approach across all methods</li>
    <li style={{ marginBottom: '6px' }}>Begin with <strong>StoryArcs</strong> to get oriented without commitment to details</li>
    <li style={{ marginBottom: '6px' }}>Experiment with different sections to see what energizes you</li>
    <li style={{ marginBottom: '6px' }}>Use the <strong>StoryHub</strong> to explore connections</li>
    <li style={{ marginBottom: '6px' }}>Don't worry about completing everything—build gradually and organically</li>
  </ul>
</div>
              </section>

              <section id="section-6" style={{ marginBottom: '48px' }}>
                <h2 style={{ fontSize: '28px', fontWeight: '600', color: '#1abc9c', marginBottom: '24px' }}>
                  Advanced Features
                </h2>
                
                <div style={{ marginBottom: '24px' }}>
  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginBottom: '16px' }}>
    Story Type Intelligence
  </h3>
  <p style={{ fontSize: '16px', color: '#555', marginBottom: '16px' }}>
    StoryUpNow adapts its guidance based on your chosen story type:
  </p>
  <ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
    <li style={{ marginBottom: '8px' }}><strong>Beat Priority System</strong>: Essential beats highlighted for your story type</li>
    <li style={{ marginBottom: '8px' }}><strong>Customized Examples</strong>: Movie and book references relevant to your narrative approach</li>
    <li style={{ marginBottom: '8px' }}><strong>Adaptive Prompts</strong>: Beat descriptions tailored to your story type's needs</li>
    <li style={{ marginBottom: '8px' }}><strong>Flexible Switching</strong>: Change story types anytime as your vision evolves</li>
  </ul>
  <p style={{ fontSize: '16px', color: '#555' }}>
    This system provides sophisticated guidance while maintaining complete creative freedom—you're enhanced, never constrained.
  </p>
</div>

<div style={{ marginBottom: '24px' }}>
  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginBottom: '16px' }}>
    The Want vs Need Framework
  </h3>
                  <p style={{ fontSize: '16px', color: '#555', marginBottom: '16px' }}>
                    This is the emotional engine that powers every compelling story:
                  </p>
                  <ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '8px' }}><strong>Want</strong>: The external goal that drives your plot forward</li>
                    <li style={{ marginBottom: '8px' }}><strong>Need</strong>: The internal growth required for true fulfillment</li>
                    <li style={{ marginBottom: '8px' }}><strong>The tension between them</strong> creates character depth and audience investment</li>
                    <li style={{ marginBottom: '8px' }}><strong>Resolution</strong> occurs when they align (or in tragedies, when they remain forever separated)</li>
                  </ul>
                  <p style={{ fontSize: '16px', color: '#555' }}>
                    This framework works regardless of genre, medium, or story type. It's the universal language of compelling character development.
                  </p>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginBottom: '16px' }}>
                    Progress Tracking
                  </h3>
                  <ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '8px' }}><strong>Completion checkboxes</strong> help you track what's done and what needs attention</li>
                    <li style={{ marginBottom: '8px' }}><strong>Visual indicators</strong> show your overall story development</li>
                    <li style={{ marginBottom: '8px' }}><strong>Section completion</strong> helps identify gaps or areas needing more work</li>
                  </ul>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginBottom: '16px' }}>
                    File Management
                  </h3>
                  <ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '8px' }}><strong>Save regularly</strong> using the Save button to preserve your work</li>
                    <li style={{ marginBottom: '8px' }}><strong>Export to PDF</strong> with the Print function for offline reference or sharing</li>
                    <li style={{ marginBottom: '8px' }}><strong>Load previous projects</strong> to continue developing existing stories</li>
                    <li style={{ marginBottom: '8px' }}><strong>Start fresh</strong> with New Project when new inspiration strikes</li>
                  </ul>
                </div>
              </section>

              <section id="section-7" style={{ marginBottom: '48px' }}>
                <h2 style={{ fontSize: '28px', fontWeight: '600', color: '#1abc9c', marginBottom: '24px' }}>
                  Troubleshooting & Support
                </h2>
                
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50', marginBottom: '12px' }}>
                    "I Don't Know Where to Start"
                  </h3>
                  <ol style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '6px' }}>Write one sentence describing your story idea</li>
                    <li style={{ marginBottom: '6px' }}>Identify what excites you most about this story</li>
                    <li style={{ marginBottom: '6px' }}>Start with whatever section relates to that excitement</li>
                    <li style={{ marginBottom: '6px' }}>Use knowledge windows when you need guidance or inspiration</li>
                  </ol>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50', marginBottom: '12px' }}>
                    "This Feels Like Too Much"
                  </h3>
                  <ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '6px' }}><strong>Pick just one level</strong>: Focus on Arcs OR Sequences OR Beats—not all three</li>
                    <li style={{ marginBottom: '6px' }}><strong>Set small goals</strong>: Work for 15-20 minutes at a time</li>
                    <li style={{ marginBottom: '6px' }}><strong>Embrace imperfection</strong>: First drafts of story development are supposed to be rough</li>
                    <li style={{ marginBottom: '6px' }}><strong>Use what serves you</strong>: Skip any section that doesn't help your specific story</li>
                  </ul>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50', marginBottom: '12px' }}>
                    "My Story Doesn't Fit This Structure"
                  </h3>
                  <ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '6px' }}><strong>Every story is unique</strong>: The framework adapts to your vision, not the other way around</li>
                    <li style={{ marginBottom: '6px' }}><strong>Focus on principles</strong>: Want vs Need works for every genre and style</li>
                    <li style={{ marginBottom: '6px' }}><strong>Use what helps</strong>: Take what serves your story and leave what doesn't</li>
                    <li style={{ marginBottom: '6px' }}><strong>Trust the process</strong>: Even unconventional stories follow emotional logic</li>
                  </ul>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50', marginBottom: '12px' }}>
                    "I Keep Getting Stuck"
                  </h3>
                  <ul style={{ fontSize: '16px', color: '#555', marginBottom: '20px', paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '6px' }}><strong>Explore knowledge windows</strong> for fresh perspectives and examples</li>
                    <li style={{ marginBottom: '6px' }}><strong>Switch sections</strong>: Sometimes character work unlocks plot problems, or theme work clarifies character</li>
                    <li style={{ marginBottom: '6px' }}><strong>Develop supporting characters</strong>: They often hold the keys to main story problems</li>
                    <li style={{ marginBottom: '6px' }}><strong>Revisit your theme</strong>: Make sure you're clear on what you're really exploring</li>
                  </ul>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50', marginBottom: '12px' }}>
                    "I'm Worried About Being Too Formulaic"
                  </h3>
                  <p style={{ fontSize: '16px', color: '#555' }}>
                    StoryUpNow provides structure, not formulas. The framework ensures your story has emotional coherence, but your unique voice, vision, and creativity determine how those emotions are expressed. Every great story—from experimental literary fiction to blockbuster entertainment—follows these underlying emotional principles while being completely distinctive in execution.
                  </p>
                </div>
              </section>

              <div style={{ 
                padding: '32px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '12px', 
                textAlign: 'center',
                border: '2px solid #1abc9c'
              }}>
                <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1abc9c', marginBottom: '16px' }}>
                  Remember
                </h2>
                <p style={{ fontSize: '18px', color: '#555', marginBottom: '16px' }}>
                  <strong>StoryUpNow isn't about forcing your creativity into a predetermined shape</strong>—it's about giving you the tools to discover what you're really trying to say and the clearest, most powerful way to say it.
                </p>
                <p style={{ fontSize: '16px', color: '#555', marginBottom: '16px' }}>
                  Your story needs emotional architecture to connect with audiences, but that architecture can support any kind of storytelling you want to do. Whether you're writing intimate character studies or epic adventures, experimental narratives or traditional genres, the principles of compelling storytelling remain consistent while allowing infinite creative expression.
                </p>
                <p style={{ fontSize: '18px', color: '#1abc9c', fontWeight: '600' }}>
                  <strong>Your story matters. Your voice is unique. StoryUpNow is here to help you tell your story in a way that honors both your creative vision and your audience's need for emotional connection.</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

const AudioPlayer = ({ audioKey, onClose }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const audioRef = useRef(null);
  
  const audio = audioContent[audioKey];
  if (!audio) return null;

  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      setIsLoading(false);
    }
  };

  const formatTime = (time) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleProgressClick = (e) => {
    if (!audioRef.current || !duration) return;
    
    const progressBar = e.currentTarget;
    const clickPosition = e.nativeEvent.offsetX / progressBar.offsetWidth;
    const newTime = clickPosition * duration;
    
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const progressPercentage = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '16px',
      boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
      border: '2px solid #9b59b6',
      minWidth: '320px',
      zIndex: 1001
    }}>
      <audio
        ref={audioRef}
        src={audio.url}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
        onCanPlay={() => setIsLoading(false)}
      />
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#2c3e50' }}>
          🎧 {audio.title}
        </h4>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '18px',
            cursor: 'pointer',
            color: '#7f8c8d'
          }}
        >
          ✕
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
        <button
          onClick={togglePlay}
          disabled={isLoading}
          style={{
            background: isLoading ? '#bdc3c7' : '#9b59b6',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            color: 'white',
            fontSize: '16px',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s ease'
          }}
        >
          {isLoading ? '⏳' : (isPlaying ? '⏸️' : '▶️')}
        </button>
        
        <div style={{ flex: 1 }}>
          {/* Progress Bar */}
          <div
            onClick={handleProgressClick}
            style={{
              width: '100%',
              height: '8px',
              backgroundColor: '#ecf0f1',
              borderRadius: '4px',
              cursor: 'pointer',
              position: 'relative',
              marginBottom: '4px'
            }}
          >
            <div
              style={{
                height: '100%',
                backgroundColor: '#9b59b6',
                borderRadius: '4px',
                width: `${progressPercentage}%`,
                transition: 'width 0.1s ease'
              }}
            />
            
            {/* Progress Dot */}
            {progressPercentage > 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: `${progressPercentage}%`,
                  width: '12px',
                  height: '12px',
                  backgroundColor: '#9b59b6',
                  borderRadius: '50%',
                  transform: 'translate(-50%, -50%)',
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
              />
            )}
          </div>
          
          {/* Time Display */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            fontSize: '12px', 
            color: '#7f8c8d',
            fontWeight: '500'
          }}>
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
      </div>
      
      <div style={{ fontSize: '12px', color: '#95a5a6', textAlign: 'center' }}>
        Click the timeline to jump to any point
      </div>
    </div>
  );
};

  const renderStoryModal = () => {
    if (!showStoryModal) return null;
    
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '32px',
          width: '900px',
          maxWidth: '95vw',
          minWidth: '600px',
          minHeight: '500px',
          maxHeight: '85vh',
          overflow: 'auto',
          boxShadow: '0 25px 50px rgba(0,0,0,0.4)',
          border: '4px solid #9b59b6',
          resize: 'both'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
  <h2 style={{ margin: 0, fontSize: '36px', fontWeight: '700', color: '#9b59b6' }}>
  Story Overview
</h2>
  
  <div style={{ display: 'flex', gap: '8px' }}>
    <span 
      onClick={() => setSelectedKnowledge('storyOverview')}
      style={{ 
        fontSize: '24px', 
        cursor: 'pointer', 
        padding: '4px',
        borderRadius: '4px',
        transition: 'all 0.2s ease',
        transform: 'scale(1)'
      }}
      onMouseEnter={(e) => {
        e.target.style.backgroundColor = '#f8f9fa';
        e.target.style.transform = 'scale(1.15)';
      }}
      onMouseLeave={(e) => {
        e.target.style.backgroundColor = 'transparent';
        e.target.style.transform = 'scale(1)';
      }}
      title="Read about story structure"
    >
      📖
    </span>
    
    <span 
      onClick={() => setCurrentAudio('storyOverview')}
      style={{ 
        fontSize: '24px', 
        cursor: 'pointer', 
        padding: '4px',
        borderRadius: '4px',
        transition: 'all 0.2s ease',
        transform: 'scale(1)'
      }}
      onMouseEnter={(e) => {
        e.target.style.backgroundColor = '#f3e5f5';
        e.target.style.transform = 'scale(1.15)';
      }}
      onMouseLeave={(e) => {
        e.target.style.backgroundColor = 'transparent';
        e.target.style.transform = 'scale(1)';
      }}
      title="Listen to story structure discussion"
    >
      🎧
    </span>
  </div>
</div>
            
            <button
              onClick={() => setShowStoryModal(false)}
              style={{
                background: '#9b59b6',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                fontSize: '24px',
                cursor: 'pointer',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
          </div>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '32px',
            overflow: 'hidden'
          }}>
            <div>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
  <h3 style={{ 
    fontSize: '24px', 
    fontWeight: '600', 
    margin: 0, 
    color: '#e74c3c' 
  }}>
    Tangible Story
  </h3>
  <span 
    onClick={() => setSelectedKnowledge('tangibleStory')}
    style={{ 
      fontSize: '20px', 
      cursor: 'pointer', 
      padding: '2px',
      borderRadius: '4px',
      transition: 'all 0.2s ease',
      transform: 'scale(1)'
    }}
    onMouseEnter={(e) => {
      e.target.style.backgroundColor = '#f8f9fa';
      e.target.style.transform = 'scale(1.15)';
    }}
    onMouseLeave={(e) => {
      e.target.style.backgroundColor = 'transparent';
      e.target.style.transform = 'scale(1)';
    }}
  >
    📖
  </span>
</div>
                <p style={{ 
                  fontSize: '14px', 
                  color: '#7f8c8d', 
                  lineHeight: '1.5',
                  marginBottom: '16px'
                }}>
                  The external journey: goals, conflicts, and events
                </p>
              </div>
              
              <textarea
                style={{
                  width: '100%',
                  padding: '20px',
                  border: '2px solid #e74c3c',
                  borderRadius: '12px',
                  fontSize: '16px',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  lineHeight: '1.6',
                  outline: 'none',
                  boxSizing: 'border-box',
                  minHeight: '200px',
                  height: '300px',
                  maxHeight: '500px'
                }}
                value={story.tangibleStory || ''}
                onChange={(e) => updateStory(null, 'tangibleStory', e.target.value)}
                placeholder="Describe the external journey of your story..."
              />
            </div>
            
            <div>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
  <h3 style={{ 
    fontSize: '24px', 
    fontWeight: '600', 
    margin: 0, 
    color: '#3498db' 
  }}>
    Thematic Story
  </h3>
  <span 
    onClick={() => setSelectedKnowledge('thematicStory')}
    style={{ 
      fontSize: '20px', 
      cursor: 'pointer', 
      padding: '2px',
      borderRadius: '4px',
      transition: 'all 0.2s ease',
      transform: 'scale(1)'
    }}
    onMouseEnter={(e) => {
      e.target.style.backgroundColor = '#f8f9fa';
      e.target.style.transform = 'scale(1.15)';
    }}
    onMouseLeave={(e) => {
      e.target.style.backgroundColor = 'transparent';
      e.target.style.transform = 'scale(1)';
    }}
  >
    📖
  </span>
</div>
                <p style={{ 
                  fontSize: '14px', 
                  color: '#7f8c8d', 
                  lineHeight: '1.5',
                  marginBottom: '16px'
                }}>
                  The internal transformation: beliefs, growth, and meaning
                </p>
              </div>
              
              <textarea
                style={{
                  width: '100%',
                  padding: '20px',
                  border: '2px solid #3498db',
                  borderRadius: '12px',
                  fontSize: '16px',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  lineHeight: '1.6',
                  outline: 'none',
                  boxSizing: 'border-box',
                  minHeight: '200px',
                  height: '300px',
                  maxHeight: '500px'
                }}
                value={story.thematicStory || ''}
                onChange={(e) => updateStory(null, 'thematicStory', e.target.value)}
                placeholder="Describe the internal journey of your story..."
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderWantNeedModal = () => {
    if (!selectedWantNeed) return null;
    
    const explanation = wantNeedExplanations[selectedWantNeed];
    
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '32px',
          width: '700px',
          maxWidth: '90vw',
          minWidth: '500px',
          maxHeight: '85vh',
          overflow: 'auto',
          boxShadow: '0 25px 50px rgba(0,0,0,0.4)',
          border: `4px solid ${explanation.color}`,
          resize: 'both'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h2 style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: explanation.color }}>
              {explanation.title}
            </h2>
            
            <button
              onClick={() => setSelectedWantNeed(null)}
              style={{
                background: explanation.color,
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                fontSize: '24px',
                cursor: 'pointer',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
          </div>
          
          <div style={{ color: '#555', lineHeight: '1.7', fontSize: '16px' }}>
  {explanation.content ? explanation.content : (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={{
        padding: '20px',
        backgroundColor: 'white',
        borderRadius: '12px',
        borderLeft: `6px solid ${explanation.color}`
      }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: '600', color: '#2c3e50' }}>
          Want (External Goal)
        </h3>
        <p style={{ margin: 0, color: '#555', lineHeight: '1.7', fontSize: '16px' }}>
          {explanation.want}
        </p>
      </div>
      
      <div style={{
        padding: '20px',
        backgroundColor: 'white',
        borderRadius: '12px',
        borderLeft: `6px solid ${explanation.color}`
      }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: '600', color: '#2c3e50' }}>
          Need (Internal Growth)
        </h3>
        <p style={{ margin: 0, color: '#555', lineHeight: '1.7', fontSize: '16px' }}>
          {explanation.need}
        </p>
      </div>
    </div>
  )}
</div>
        </div>
      </div>
    );
  };

  const renderArcEditor = () => {
    if (!selectedArc) return null;
    
    const arc = arcDetails[selectedArc];
    const arcData = story.miniArcs[selectedArc] || { notes: '', completed: false };
    
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '32px',
          width: '800px',
          minWidth: '400px',
          minHeight: '300px',
          maxHeight: '85vh',
          overflow: 'auto',
          boxShadow: '0 25px 50px rgba(0,0,0,0.4)',
          border: `4px solid ${arc.color}`,
          resize: 'both'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h2 style={{ margin: 0, fontSize: '36px', fontWeight: '700', color: arc.color }}>
                  {arc.name}
                </h2>
                <span 
                  onClick={() => setSelectedKnowledge(selectedArc)}
                  style={{ 
                    fontSize: '24px', 
                    cursor: 'pointer', 
                    padding: '4px',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                    transform: 'scale(1)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f8f9fa';
                    e.target.style.transform = 'scale(1.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  📖
                </span>
              </div>
              <p style={{ margin: '8px 0', fontSize: '18px', color: '#7f8c8d', fontStyle: 'italic' }}>
                {arc.subtitle}
              </p>
            </div>
            
            <button
              onClick={() => setSelectedArc(null)}
              style={{
                background: arc.color,
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                fontSize: '24px',
                cursor: 'pointer',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
          </div>
          
          <div style={{ 
            padding: '20px', 
            backgroundColor: arc.lightColor, 
            borderRadius: '12px',
            marginBottom: '24px',
            borderLeft: `6px solid ${arc.color}`
          }}>
            <p style={{ margin: 0, color: '#555', lineHeight: '1.7', fontSize: '16px' }}>
              {getArcPrompt(selectedArc) || arc.description}
            </p>
          </div>
          
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px', 
              marginBottom: '20px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '500'
            }}>
              <input
                type="checkbox"
                checked={arcData.completed}
                onChange={(e) => updateArc(selectedArc, 'completed', e.target.checked)}
                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
              />
              Mark as completed
            </label>
            
            <label style={{ display: 'block', fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#2c3e50' }}>
              Notes for {arc.name}:
            </label>
            <textarea
              style={{
                width: '100%',
                padding: '20px',
                border: `2px solid ${arc.color}`,
                borderRadius: '12px',
                height: '300px',
                fontSize: '16px',
                resize: 'vertical',
                fontFamily: 'inherit',
                lineHeight: '1.6',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              value={arcData.notes}
              onChange={(e) => updateArc(selectedArc, 'notes', e.target.value)}
              placeholder={`Write your notes for this arc...`}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderBeatEditor = () => {
    if (!selectedBeat) return null;
    
    const beat = beatDetails[selectedBeat];
    const beatData = story.storyBeats[selectedBeat] || { notes: '', completed: false };
    
    let colorScheme;
    if (individualBeatColors[selectedBeat]) {
      colorScheme = individualBeatColors[selectedBeat];
    } else {
      const arcKey = beat.arc.toLowerCase().replace(' ', '');
      colorScheme = arcDetails[arcKey] || { color: '#6c757d', lightColor: '#f8f9fa' };
    }
    
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '32px',
          width: '700px',
          minWidth: '400px',
          minHeight: '300px',
          maxHeight: '85vh',
          overflow: 'auto',
          boxShadow: '0 25px 50px rgba(0,0,0,0.4)',
          border: `4px solid ${colorScheme.color}`,
          resize: 'both'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '28px', fontWeight: '600', color: '#2c3e50' }}>
                  {getPriorityMedal(selectedBeat, 'beat')}{beat.name}
                </h3>
                <span 
                  onClick={() => setSelectedKnowledge(selectedBeat)}
                  style={{ 
                    fontSize: '20px', 
                    cursor: 'pointer', 
                    padding: '4px',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                    transform: 'scale(1)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f8f9fa';
                    e.target.style.transform = 'scale(1.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  📖
                </span>
              </div>
              <p style={{ margin: '8px 0', color: colorScheme.color, fontSize: '16px', fontWeight: '500' }}>
                {beat.arc} Arc
              </p>
            </div>
            
            <button
              onClick={() => setSelectedBeat(null)}
              style={{
                background: colorScheme.color,
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                fontSize: '24px',
                cursor: 'pointer',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
          </div>
          
          <div style={{ 
            padding: '20px', 
            backgroundColor: colorScheme.lightColor, 
            borderRadius: '12px',
            marginBottom: '24px',
            borderLeft: `6px solid ${colorScheme.color}`
          }}>
            <p style={{ margin: 0, color: '#555', lineHeight: '1.7', fontSize: '16px' }}>
              {beat.description}
            </p>
          </div>
          
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
  display: 'flex', 
  alignItems: 'center', 
  gap: '12px', 
  marginBottom: '20px',
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: '500'
}}>
  <input
    type="checkbox"
    checked={beatData.completed}
    onChange={(e) => updateStoryBeat(selectedBeat, 'completed', e.target.checked)}
    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
  />
  Mark as completed
</label>
            
            <label style={{ display: 'block', fontSize: '18px', fontWeight: '500', marginBottom: '12px', color: '#2c3e50' }}>
              Notes:
            </label>
            <textarea
              style={{
                width: '100%',
                padding: '16px',
                border: `2px solid ${colorScheme.color}`,
                borderRadius: '12px',
                height: '250px',
                fontSize: '16px',
                resize: 'vertical',
                outline: 'none',
                lineHeight: '1.5',
                boxSizing: 'border-box'
              }}
              value={beatData.notes}
              onChange={(e) => updateStoryBeat(selectedBeat, 'notes', e.target.value)}
              placeholder={getBeatPrompt(selectedBeat)}
            />
          </div>
        </div>
      </div>
    );
  };

  // Add hover state for navigation buttons
  const [hoveredButton, setHoveredButton] = useState(null);

  const buttonStyle = {
    color: 'white',
    border: 'none',
    padding: '12px 16px',
    borderRadius: '25px',
    cursor: 'pointer',
    boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.2)',
    fontSize: '14px',
    fontWeight: '600',
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    height: '44px',
    boxShadow: 'none',
    transition: 'all 0.3s ease',
    textDecoration: 'none',
    margin: 0,
    boxSizing: 'border-box',
    backgroundColor: 'transparent'
  };

  // Navigation button hover style
  const getNavHoverStyle = (buttonId) => ({
    ...buttonStyle,
    transform: hoveredButton === buttonId ? 'scale(1.05)' : 'scale(1)',
    transformOrigin: 'center center'
  });

  // Login screen
  if (!isAuthenticated) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '60px',
          borderRadius: '20px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
          textAlign: 'center',
          maxWidth: '400px',
          width: '90%'
        }}>
          <div style={{ marginBottom: '30px' }}>
            <img 
              src="/storyupnow-logo-white-bg.png" 
              alt="StoryUpNow Logo" 
              style={{ 
                width: '180px', 
                height: '180px', 
                marginBottom: '20px',
                objectFit: 'contain' 
              }}
            />
          </div>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              placeholder="Enter beta password"
              style={{
                width: '100%',
                padding: '16px',
                fontSize: '18px',
                border: '2px solid #dee2e6',
                borderRadius: '12px',
                marginBottom: '24px',
                outline: 'none',
                textAlign: 'center',
                boxSizing: 'border-box'
              }}
            />
            <button type="submit" style={{ width: '100%', padding: '16px', fontSize: '18px', background: '#3498db', color: 'white', border: 'none', borderRadius: '12px', cursor: 'pointer' }}>
              Access StoryUpNow
            </button>
          </form>
          
          <p style={{
            fontSize: '11px',
            color: '#666',
            marginTop: '20px',
            lineHeight: '1.4',
            textAlign: 'center',
            maxWidth: '100%',
            margin: '20px auto 0 auto'
          }}>
            By entering this application, you agree not to copy, reproduce, distribute, or use any content, methodologies, or materials from StoryUpNow without written permission.
          </p>
        </div>
      </div>
    );
  }

  // Main application
  return (
    <div style={{ height: '100vh', backgroundColor: '#f5f5f5', display: 'flex', overflow: 'hidden' }}>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
          
          * {
            font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          }
        `}
      </style>
      <nav style={{ 
        background: 'linear-gradient(180deg, #2c3e50 0%, #34495e 100%)', 
        color: 'white', 
        width: '220px',
        height: '100vh',
        padding: '20px 20px',
        boxShadow: '4px 0 20px rgba(0,0,0,0.1)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}>
       <div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '700' }}>
              StoryUpNow
            </h1>
          </div>
          
          <button 
  onClick={() => setShowGuideModal(true)}
  onMouseEnter={() => setHoveredButton('start-here')}
  onMouseLeave={() => setHoveredButton(null)}
  style={{
    ...getNavHoverStyle('start-here'),
    background: 'linear-gradient(135deg, #1abc9c 0%, #fdd835 100%)',
    borderRadius: '25px',
    boxShadow: hoveredButton === 'start-here' ? '0 8px 25px rgba(26, 188, 156, 0.4)' : 'none',
    filter: hoveredButton === 'start-here' ? 'brightness(1.1)' : 'none',
    marginTop: '16px',
    marginBottom: '32px',
    fontSize: '16px',
    fontWeight: '700',
    padding: '14px 16px'
  }}
>
  🚀 Start Here
</button>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {/* File Operations Group */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                Project
              </h3>
              <button 
                onClick={startNewProject}
                onMouseEnter={() => setHoveredButton('new-project')}
                onMouseLeave={() => setHoveredButton(null)}
                style={{
                  ...getNavHoverStyle('new-project'),
                  background: 'linear-gradient(135deg, #ff4757 0%, #c44569 100%)',
                  borderRadius: '25px',
                  boxShadow: hoveredButton === 'new-project' ? '0 8px 25px rgba(255, 71, 87, 0.4)' : 'none',
                  filter: hoveredButton === 'new-project' ? 'brightness(1.1)' : 'none'
                }}
              >
                📄 New Story
              </button>
              
              <label 
                onMouseEnter={() => setHoveredButton('load-story')}
                onMouseLeave={() => setHoveredButton(null)}
                style={{
                  ...getNavHoverStyle('load-story'),
                  background: 'linear-gradient(135deg, #8854d0 0%, #3742fa 100%)',
                  borderRadius: '25px',
                  boxShadow: hoveredButton === 'load-story' ? '0 8px 25px rgba(135, 84, 208, 0.4)' : 'none',
                  filter: hoveredButton === 'load-story' ? 'brightness(1.1)' : 'none'
                }}
              >
                📂 Open Story
                <input
                  type="file"
                  accept=".json"
                  onChange={loadStory}
                  style={{ display: 'none' }}
                />
              </label>
              
              <button 
                onClick={saveStory}
                onMouseEnter={() => setHoveredButton('save-story')}
                onMouseLeave={() => setHoveredButton(null)}
                style={{
                  ...getNavHoverStyle('save-story'),
                  background: 'linear-gradient(135deg, #20bf6b 0%, #1e90ff 100%)',
                  borderRadius: '25px',
                  boxShadow: hoveredButton === 'save-story' ? '0 8px 25px rgba(32, 191, 107, 0.4)' : 'none',
                  filter: hoveredButton === 'save-story' ? 'brightness(1.1)' : 'none'
                }}
              >
                💾 Save Story
              </button>
              
              <button 
                onClick={printStory}
                onMouseEnter={() => setHoveredButton('print-story')}
                onMouseLeave={() => setHoveredButton(null)}
                style={{
                  ...getNavHoverStyle('print-story'),
                  background: 'linear-gradient(135deg, #ff9f43 0%, #ff6b6b 100%)',
                  borderRadius: '25px',
                  boxShadow: hoveredButton === 'print-story' ? '0 8px 25px rgba(255, 159, 67, 0.4)' : 'none',
                  filter: hoveredButton === 'print-story' ? 'brightness(1.1)' : 'none'
                }}
              >
                🖨️ Print Story
              </button>
            </div>
            
            {/* Navigation Group */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                Pages
              </h3>
              <button 
                onClick={() => setActiveView('wheel')} 
                onMouseEnter={() => setHoveredButton('story-hub')}
                onMouseLeave={() => setHoveredButton(null)}
                style={{
                  ...getNavHoverStyle('story-hub'),
                  background: activeView === 'wheel' 
                    ? 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)' 
                    : (hoveredButton === 'story-hub' ? 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)' : 'linear-gradient(135deg, #54a0ff 0%, #2e86de 100%)'),
                  borderRadius: '25px',
                  fontWeight: activeView === 'wheel' ? '700' : '600',
                  boxShadow: (hoveredButton === 'story-hub' || activeView === 'wheel') ? '0 8px 25px rgba(84, 160, 255, 0.4)' : 'none',
                  filter: hoveredButton === 'story-hub' ? 'brightness(1.1)' : 'none'
                }}
              >
                StoryHub
              </button>
             <button 
                onClick={() => setActiveView('theme')} 
                onMouseEnter={() => setHoveredButton('theme')}
                onMouseLeave={() => setHoveredButton(null)}
                style={{
                  ...getNavHoverStyle('theme'),
                  background: activeView === 'theme' 
                    ? 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)' 
                    : 'linear-gradient(135deg, #5882a0 0%, #4a7390 100%)',
                  boxShadow: activeView === 'theme' 
                    ? '0 8px 25px rgba(52, 152, 219, 0.4)' 
                    : '0 6px 20px rgba(88, 130, 160, 0.3)',
                  transform: activeView === 'theme' 
                    ? (hoveredButton === 'theme' ? 'scale(1.05) translateY(-1px)' : 'translateY(-1px)') 
                    : (hoveredButton === 'theme' ? 'scale(1.05) translateY(0px)' : 'translateY(0px)')
                }}
              >
                Theme
              </button>
              <button 
                onClick={() => setActiveView('characters')} 
                onMouseEnter={() => setHoveredButton('characters')}
                onMouseLeave={() => setHoveredButton(null)}
                style={{
                  ...getNavHoverStyle('characters'),
                  background: activeView === 'characters' 
                    ? 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)' 
                    : 'linear-gradient(135deg, #5882a0 0%, #4a7390 100%)',
                  boxShadow: activeView === 'characters' 
                    ? '0 8px 25px rgba(52, 152, 219, 0.4)' 
                    : '0 6px 20px rgba(88, 130, 160, 0.3)',
                  transform: activeView === 'characters' 
                    ? (hoveredButton === 'characters' ? 'scale(1.05) translateY(-1px)' : 'translateY(-1px)') 
                    : (hoveredButton === 'characters' ? 'scale(1.05) translateY(0px)' : 'translateY(0px)')
                }}
              >
                Characters
              </button>
              <button 
                onClick={() => setActiveView('structure')} 
                onMouseEnter={() => setHoveredButton('structure')}
                onMouseLeave={() => setHoveredButton(null)}
                style={{
                  ...getNavHoverStyle('structure'),
                  background: activeView === 'structure' 
                    ? 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)' 
                    : 'linear-gradient(135deg, #5882a0 0%, #4a7390 100%)',
                  boxShadow: activeView === 'structure' 
                    ? '0 8px 25px rgba(52, 152, 219, 0.4)' 
                    : '0 6px 20px rgba(88, 130, 160, 0.3)',
                  transform: activeView === 'structure' 
                    ? (hoveredButton === 'structure' ? 'scale(1.05) translateY(-1px)' : 'translateY(-1px)') 
                    : (hoveredButton === 'structure' ? 'scale(1.05) translateY(0px)' : 'translateY(0px)')
                }}
              >
                StoryArcs
              </button>
              <button 
                onClick={() => setActiveView('sequences')} 
                onMouseEnter={() => setHoveredButton('sequences')}
                onMouseLeave={() => setHoveredButton(null)}
                style={{
                  ...getNavHoverStyle('sequences'),
                  background: activeView === 'sequences' 
                    ? 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)' 
                    : 'linear-gradient(135deg, #5882a0 0%, #4a7390 100%)',
                  boxShadow: activeView === 'sequences' 
                    ? '0 8px 25px rgba(52, 152, 219, 0.4)' 
                    : '0 6px 20px rgba(88, 130, 160, 0.3)',
                  transform: activeView === 'sequences' 
                    ? (hoveredButton === 'sequences' ? 'scale(1.05) translateY(-1px)' : 'translateY(-1px)') 
                    : (hoveredButton === 'sequences' ? 'scale(1.05) translateY(0px)' : 'translateY(0px)')
                }}
              >
                StorySequences
              </button>
              <button 
                onClick={() => setActiveView('beats')} 
                onMouseEnter={() => setHoveredButton('beats')}
                onMouseLeave={() => setHoveredButton(null)}
                style={{
                  ...getNavHoverStyle('beats'),
                  background: activeView === 'beats' 
                    ? 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)' 
                    : 'linear-gradient(135deg, #5882a0 0%, #4a7390 100%)',
                  boxShadow: activeView === 'beats' 
                    ? '0 8px 25px rgba(52, 152, 219, 0.4)' 
                    : '0 6px 20px rgba(88, 130, 160, 0.3)',
                  transform: activeView === 'beats' 
                    ? (hoveredButton === 'beats' ? 'scale(1.05) translateY(-1px)' : 'translateY(-1px)') 
                    : (hoveredButton === 'beats' ? 'scale(1.05) translateY(0px)' : 'translateY(0px)')
                }}
              >
                StoryBeats
              </button>
            </div>
            

          </div>
        </div>
      </nav>
      
      <div style={{ flex: 1, overflow: 'auto' }}>
        {activeView === 'wheel' && renderProfessionalStoryWheel()}
        {activeView === 'characters' && (
          <Characters 
            story={story}
            updateStory={updateStory}
            addSecondaryCharacter={addSecondaryCharacter}
            updateSecondaryCharacter={updateSecondaryCharacter}
            removeSecondaryCharacter={removeSecondaryCharacter}
            setSelectedKnowledge={setSelectedKnowledge}
          />
        )}
        {activeView === 'structure' && renderStructure()}
       {activeView === 'sequences' && renderSequences()}
        {activeView === 'beats' && renderBeats()}
        {activeView === 'theme' && renderTheme()}
      </div>
      
      {renderBeatEditor()}
      {renderSequenceEditor()}
      {renderArcEditor()}
      {renderStoryModal()}
      {renderGuideModal()}
      {renderWantNeedModal()}
      {renderKnowledgeModal()}
      {renderStoryTypeModal()}
      {renderKnowledgeModal()}
      {currentAudio && (
        <AudioPlayer
          audioKey={currentAudio}
          onClose={() => setCurrentAudio(null)}
        />
      )}
    </div>
  );
}

export default App;